import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge } from 'react-bootstrap';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import Breadcrumbs, { BreadcrumbItem } from '../../Common/Breadcrumbs';
import { getTournamentById } from '../../api/tournament/tournamentService';
import { PaymentPreviewResponse, addTournamentRegistration, createTournamentPaymentOrder, addPlayersMidSeason, getPaymentPreview } from '../../api/tournament/paymentService';
import { initiatePaymentWithKey } from '../../lib/razorpay';
import './PaymentPage.css';
import { getImageUrl } from '../../utils/imageUrl';

// Import the SFL logo and profile photo
import sflLogo from '../../assets/images/SFL Logo Blue.png';
import profilePhoto from '../../assets/images/profile photo.png';

interface Player {
  _id: string;
  name: string;
  profilePic: string;
  dob: string;
  ageCategory: string;
  verifyStat: 'Approved' | 'Referred Back' | 'Pending';
  paymentStat: 'Paid' | 'Not Paid' | 'Pending';
  amountPaid: number;
  amountDue: number;
  isSelected: boolean;
}

interface TeamPayment {
  _id: string;
  name: string;
  logo: string;
  ageGroup: string;
  playersCount: number;
  playerFee: number;
  firstPayment: number;
  amountPending: number;
  revisedAmount: number;
  secondPayment: number;
  dueDate: string;
  paymentOption: 'total' | 'minimum' | 'custom';
  customAmount?: number;
  players?: Player[];
  customAmountError?: string; // Add error state for custom amount
  customAmountTouched?: boolean; // Add touched state for validation
}

interface SelectedTeam {
  _id: string;
  name: string;
  shortName: string;
  logo: string;
  ageGroups: string[];
  coachManagerName: string;
  contactDetails: string;
  players: number;
  verifiedPlayers: number;
  paidPlayers: number;
  isSelected: boolean;
}

interface BillItem {
  description: string;
  amount: number;
}

interface PaymentPreviewData {
  tournament: string;
  team: string;
  perPlayerFee: number;
  playerCount: number;
  totalAmount: number;
  fullAmount: number;
  amountPaid: number;
  minimumAmountToPay: number;
  amountToPay: number; // Add amountToPay field for dues payments
  balanceAmount: number;
  milestoneCompleted: string | null;
}

interface PaymentPageProps {
  paymentMode?: 'online' | 'offline';
  isRecordPayment?: boolean;
}

const PaymentPage: React.FC<PaymentPageProps> = ({ 
  paymentMode = 'online', 
  isRecordPayment = false 
}) => {
  const { id: tournamentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: any) => state.Login);
  
  // State management
  const [tournament, setTournament] = useState<any>(null);
  const [teamPayments, setTeamPayments] = useState<TeamPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeams, setSelectedTeams] = useState<SelectedTeam[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<Player[]>([]);
  const [paymentSource, setPaymentSource] = useState<'register' | 'playerManagement' | 'dues'>('playerManagement');
  
  // Payment preview fetching for record payment mode
  const fetchPaymentPreviewForRecordPayment = async () => {
    if (isRecordPayment && location.state?.team && location.state?.tournamentId) {
      try {
        const { team, tournamentId } = location.state;
        const ageCategory = team.ageGroups?.[0] || 'U10';
        const registrationId = team.registration || null;
        
        console.log('🔍 PaymentPage - Using standard payment preview API for all teams');
        console.log('🔍 Registration ID:', registrationId);
        console.log('🔍 Age Category:', ageCategory);
        
        // Always use the standard payment preview API with registrationId in body
          const response = await getPaymentPreview(
            tournamentId,
            team._id,
          { 
            registrationId: registrationId, // null for new teams, actual ID for existing registrations
            ageCategory, 
            players: selectedPlayers.map(p => p._id) 
          }
          );
          setPaymentPreviewData(response.data);
      } catch (error) {
        console.error('Error fetching payment preview for record payment:', error);
      }
    }
  };
  const [paymentPreviewData, setPaymentPreviewData] = useState<PaymentPreviewData | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  
  // Payment method state for offline payments
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  
  // Payment method options
  const paymentMethodOptions = [
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'neft', label: 'NEFT' },
    { value: 'imps', label: 'IMPS' },
    { value: 'check', label: 'Check' }
  ];
  
  // SECURITY: Rate limiting state
  const [lastPaymentAttempt, setLastPaymentAttempt] = useState<number>(0);
  const PAYMENT_COOLDOWN_MS = 2000; // 2 seconds between payment attempts

  // Mock data for team payments
  const mockTeamPayments: TeamPayment[] = [
    {
      _id: '1',
      name: 'Oranje FC',
      logo: profilePhoto,
      ageGroup: 'U12',
      playersCount: 4,
      playerFee: 1000,
      firstPayment: 2000,
      amountPending: 0,
      revisedAmount: 8000,
      secondPayment: 10500,
      dueDate: '30 July 2025',
      paymentOption: 'minimum'
    },
    {
      _id: '2',
      name: 'Community FC',
      logo: profilePhoto,
      ageGroup: 'U12',
      playersCount: 4,
      playerFee: 1000,
      firstPayment: 2000,
      amountPending: 0,
      revisedAmount: 8000,
      secondPayment: 10500,
      dueDate: '30 July 2025',
      paymentOption: 'total'
    }
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Set payment source based on available data
  useEffect(() => {
    if (location.state?.source === 'dues') {
      console.log('🔍 Setting paymentSource to dues based on source');
      setPaymentSource('dues');
    } else if (location.state?.selectedTeams && location.state.selectedTeams.length > 0) {
      console.log('🔍 Setting paymentSource to register based on selectedTeams');
      setPaymentSource('register');
    } else if (location.state?.selectedPlayers && location.state?.team) {
      console.log('🔍 Setting paymentSource to playerManagement based on selectedPlayers');
      setPaymentSource('playerManagement');
    } else if (location.state?.isRecordPayment) {
      console.log('🔍 Setting paymentSource to record payment mode');
      setPaymentSource('playerManagement');
    }
  }, [location.state]);
  
  // Fetch payment preview for record payment mode
  useEffect(() => {
    if (isRecordPayment && selectedPlayers.length > 0) {
      fetchPaymentPreviewForRecordPayment();
    }
  }, [selectedPlayers, isRecordPayment]);

  const loadData = async () => {
    try {
      console.log('PaymentPage - Location state:', location.state);
      console.log('PaymentPage - Team data in location state:', location.state?.team);
      console.log('PaymentPage - Team ID in location state:', location.state?.teamId);
      console.log('PaymentPage - Selected teams:', location.state?.selectedTeams);
      console.log('PaymentPage - Payment preview data:', location.state?.paymentPreview);
      
      // Check if we have data from Record Payment mode - PRIORITY 1
      if (isRecordPayment && location.state?.team) {
        console.log('🔍 Record Payment - Loading team data for record payment');
        const team = location.state.team;
        setSelectedTeams([{
          _id: team._id,
          name: team.name,
          shortName: team.shortName,
          logo: team.logo,
          ageGroups: team.ageGroups,
          coachManagerName: team.coachManagerName,
          contactDetails: team.contactDetails,
          players: team.players || 0,
          verifiedPlayers: 0,
          paidPlayers: 0,
          isSelected: true
        }]);
        if (location.state.tournament) {
          // Use tournament data from location state instead of making API call
          setTournament(location.state.tournament);
        } else if (location.state.tournamentId) {
          // Fallback to API call only if tournament data is not available
          const tournamentData = await getTournamentById(location.state.tournamentId);
          setTournament(tournamentData);
        }
        if (location.state.selectedPlayers) {
          setSelectedPlayers(location.state.selectedPlayers);
        }
        if (location.state.paymentPreview) {
          setPaymentPreviewData(location.state.paymentPreview);
          
          // ✅ ADD: Create teamPayments array for record payment flow
          const paymentData = location.state.paymentPreview;
          const hasValidPaymentData = paymentData.totalAmount && paymentData.perPlayerFee;
          
          if (hasValidPaymentData) {
            // Create team payment with real payment data from API
            const teamPayment: TeamPayment = {
              _id: team._id,
              name: team.name,
              logo: team.logo,
              ageGroup: team.ageGroups[0] || 'U12',
              playersCount: paymentData.playerCount || location.state.selectedPlayers?.length || 0,
              playerFee: paymentData.perPlayerFee,
              firstPayment: paymentData.minimumAmountToPay || paymentData.amountToPay || 0,
              amountPending: paymentData.amountPaid || 0,
              revisedAmount: paymentData.totalAmount,
              secondPayment: paymentData.balanceAmount || paymentData.amountToPay || 0,
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: location.state.selectedPlayers || []
            };
            
            console.log('🔍 Record Payment - Created team payment with API data:', {
              playerCount: paymentData.playerCount,
              perPlayerFee: paymentData.perPlayerFee,
              totalAmount: paymentData.totalAmount,
              amountPaid: paymentData.amountPaid,
              minimumAmountToPay: paymentData.minimumAmountToPay,
              balanceAmount: paymentData.balanceAmount,
              amountToPay: paymentData.amountToPay
            });
            setTeamPayments([teamPayment]);
          } else {
            console.log('🔍 Record Payment - Payment data has null values, using fallback calculations');
            // Fallback to calculated amounts when API returns null
            const fallbackPlayerFee = 1000; // Default fee
            const fallbackPlayerCount = location.state.selectedPlayers?.length || 0;
            const fallbackTotalAmount = fallbackPlayerCount * fallbackPlayerFee;
            
            const teamPayment: TeamPayment = {
              _id: team._id,
              name: team.name,
              logo: team.logo,
              ageGroup: team.ageGroups[0] || 'U12',
              playersCount: fallbackPlayerCount,
              playerFee: fallbackPlayerFee,
              firstPayment: fallbackTotalAmount * 0.25, // 25% as first payment
              amountPending: paymentData.amountPaid || 0,
              revisedAmount: fallbackTotalAmount,
              secondPayment: fallbackTotalAmount * 0.75, // 75% as second payment
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: location.state.selectedPlayers || []
            };
            
            setTeamPayments([teamPayment]);
          }
        } else {
          console.log('🔍 Record Payment - No payment preview data found, using fallback');
          // Fallback when no payment preview data is available
          const fallbackPlayerFee = 1000; // Default fee
          const fallbackPlayerCount = location.state.selectedPlayers?.length || 0;
          const fallbackTotalAmount = fallbackPlayerCount * fallbackPlayerFee;
          
          const teamPayment: TeamPayment = {
            _id: team._id,
            name: team.name,
            logo: team.logo,
            ageGroup: team.ageGroups[0] || 'U12',
            playersCount: fallbackPlayerCount,
            playerFee: fallbackPlayerFee,
            firstPayment: fallbackTotalAmount * 0.25, // 25% as first payment
            amountPending: 0,
            revisedAmount: fallbackTotalAmount,
            secondPayment: fallbackTotalAmount * 0.75, // 75% as second payment
            dueDate: '30 July 2025',
            paymentOption: 'total' as const,
            customAmount: undefined,
            players: location.state.selectedPlayers || []
          };
          
          setTeamPayments([teamPayment]);
        }
      }
      // Check if we have data from Dues (dues flow) - PRIORITY 2
      else if (location.state?.source === 'dues') {
        console.log('PaymentPage - Loading from Dues screen:', location.state.due);
        console.log('🔍 PaymentPage - Dues data structure:', {
          due: location.state.due,
          paymentPreview: location.state.paymentPreview,
          tournament: location.state.tournament,
          team: location.state.team,
          registrationId: location.state.registrationId
        });
        setPaymentSource('dues');
        
        // Check if we have payment preview data from Dues
        if (location.state?.paymentPreview) {
          console.log('PaymentPage - Using real payment preview data from Dues:', location.state.paymentPreview);
          
          // Only set paymentPreviewData if we have valid data
          const paymentData = location.state.paymentPreview;
          const hasValidPaymentData = paymentData.totalAmount && paymentData.perPlayerFee;
          
          if (hasValidPaymentData) {
            setPaymentPreviewData(location.state.paymentPreview);
          } else {
            console.log('PaymentPage - Payment preview data has null values, not setting paymentPreviewData');
            setPaymentPreviewData(null);
          }
          
          const due = location.state.due;
          
          console.log('PaymentPage - Has valid payment data:', hasValidPaymentData);
          console.log('PaymentPage - Payment data details:', {
            totalAmount: paymentData.totalAmount,
            perPlayerFee: paymentData.perPlayerFee,
            playerCount: paymentData.playerCount,
            amountPaid: paymentData.amountPaid,
            minimumAmountToPay: paymentData.minimumAmountToPay,
            amountToPay: paymentData.amountToPay,
            balanceAmount: paymentData.balanceAmount
          });
          
          if (hasValidPaymentData) {
            // Create team payment with real payment data from API
            const teamPayment: TeamPayment = {
              _id: due.teamInfo._id,
              name: due.teamInfo.name,
              logo: due.teamInfo.logo,
              ageGroup: due.ageCategory,
              playersCount: paymentData.playerCount, // Use API playerCount
              playerFee: paymentData.perPlayerFee, // Use API perPlayerFee
              firstPayment: paymentData.minimumAmountToPay, // Use API minimumAmountToPay
              amountPending: paymentData.amountPaid || 0, // Use API amountPaid
              revisedAmount: paymentData.totalAmount, // Use API totalAmount
              secondPayment: paymentData.balanceAmount, // Use API balanceAmount
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: [] // No specific players for dues payment
            };
            
            console.log('🔍 PaymentPage - Created team payment with API data from Dues:', {
              playerCount: paymentData.playerCount,
              perPlayerFee: paymentData.perPlayerFee,
              totalAmount: paymentData.totalAmount,
              amountPaid: paymentData.amountPaid,
              minimumAmountToPay: paymentData.minimumAmountToPay,
              balanceAmount: paymentData.balanceAmount
            });
            setTeamPayments([teamPayment]);
            setSelectedPlayers([]);
          } else {
            console.log('PaymentPage - Payment data has null values, using fallback calculations');
            // Fallback to calculated amounts when API returns null
            const fallbackPlayerFee = 1000; // Default fee
            const fallbackTotalAmount = paymentData.playerCount * fallbackPlayerFee;
            
            const teamPayment: TeamPayment = {
              _id: due.teamInfo._id,
              name: due.teamInfo.name,
              logo: due.teamInfo.logo,
              ageGroup: due.ageCategory,
              playersCount: paymentData.playerCount,
              playerFee: fallbackPlayerFee,
              firstPayment: fallbackTotalAmount * 0.25, // 25% as first payment
              amountPending: paymentData.amountPaid || 0, // Use amount already paid if available
              revisedAmount: fallbackTotalAmount, // Calculated total amount
              secondPayment: fallbackTotalAmount * 0.75, // 75% as second payment
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: [] // No specific players for dues payment
            };
            
            setTeamPayments([teamPayment]);
            setSelectedPlayers([]);
          }
        } else {
          console.log('🔍 PaymentPage - No payment preview data found for dues');
          // Fallback to due data
          const due = location.state.due;
          
          // ✅ FIX: Calculate next due date from milestone data instead of hardcoding
          let nextDueDate = 'TBD';
          if (due.milestoneDueDates && due.milestoneDueDates.length > 0) {
            // Find the next unpaid milestone due date
            const nextMilestone = due.milestoneDueDates.find((milestone: any) => {
              const dueDate = new Date(milestone.dueDate);
              const today = new Date();
              return dueDate >= today; // Future or today's due date
            });
            
            if (nextMilestone) {
              const dueDate = new Date(nextMilestone.dueDate);
              nextDueDate = dueDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              });
              console.log('🔍 PaymentPage - Calculated next due date:', nextDueDate, 'from milestone:', nextMilestone);
            } else {
              // If no future milestone, use the last milestone
              const lastMilestone = due.milestoneDueDates[due.milestoneDueDates.length - 1];
              if (lastMilestone) {
                const dueDate = new Date(lastMilestone.dueDate);
                nextDueDate = dueDate.toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                });
                console.log('🔍 PaymentPage - Using last milestone due date:', nextDueDate);
              }
            }
          }
          
          const teamPayment: TeamPayment = {
            _id: due.teamInfo._id,
            name: due.teamInfo.name,
            logo: due.teamInfo.logo,
            ageGroup: due.ageCategory,
            playersCount: due.playerCount,
            playerFee: 1000, // Default fee
            firstPayment: due.balanceAmount * 0.25, // 25% of balance
            amountPending: due.amountPaid || 0,
            revisedAmount: due.totalAmount,
            secondPayment: due.balanceAmount * 0.75, // 75% of balance
            dueDate: nextDueDate, // ✅ Use calculated due date
            paymentOption: 'total' as const,
            customAmount: undefined,
            players: []
          };
          
          setTeamPayments([teamPayment]);
          setSelectedPlayers([]);
        }
      }
      // Check if we have data from TeamRegistration (register flow) - PRIORITY 2
      if (location.state?.selectedTeams && location.state.selectedTeams.length > 0) {
        console.log('PaymentPage - Loading teams from Register screen:', location.state.selectedTeams);
        console.log('🔍 PaymentPage - Register teams data structure:', {
          selectedTeams: location.state.selectedTeams,
          paymentPreview: location.state.paymentPreview,
          unpaidPlayers: location.state.unpaidPlayers,
          unpaidPlayerIds: location.state.unpaidPlayerIds,
          tournament: location.state.tournament,
          team: location.state.team,
          teamId: location.state.teamId
        });
        setSelectedTeams(location.state.selectedTeams);
        setPaymentSource('register');
        
        // Check if we have payment preview data from TeamRegistration
        if (location.state?.paymentPreview) {
          console.log('PaymentPage - Using real payment preview data from TeamRegistration:', location.state.paymentPreview);
          
          // Get the team from selected teams
          const team = location.state.selectedTeams[0];
          const paymentData = location.state.paymentPreview;
          
          // Check if payment data has valid amounts (removed fullAmount check as it's not in API response)
          const hasValidPaymentData = paymentData.totalAmount && 
                                    paymentData.perPlayerFee;
          
          console.log('PaymentPage - Has valid payment data:', hasValidPaymentData);
          console.log('PaymentPage - Payment data details:', {
            totalAmount: paymentData.totalAmount,
            perPlayerFee: paymentData.perPlayerFee,
            playerCount: paymentData.playerCount,
            amountPaid: paymentData.amountPaid,
            minimumAmountToPay: paymentData.minimumAmountToPay,
            amountToPay: paymentData.amountToPay,
            balanceAmount: paymentData.balanceAmount
          });
          
          if (hasValidPaymentData) {
            // Set payment preview data only when we have valid data
            setPaymentPreviewData(location.state.paymentPreview);
            
            // Create team payment with real payment data from API
            const teamPayment: TeamPayment = {
              _id: team._id,
              name: team.name,
              logo: team.logo,
              ageGroup: team.ageGroups.join(', '),
              playersCount: paymentData.playerCount, // Use API playerCount
              playerFee: paymentData.perPlayerFee, // Use API perPlayerFee
              firstPayment: paymentData.minimumAmountToPay, // Use API minimumAmountToPay
              amountPending: paymentData.amountPaid || 0, // Use API amountPaid
              revisedAmount: paymentData.totalAmount, // Use API totalAmount
              secondPayment: paymentData.balanceAmount, // Use API balanceAmount
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: location.state.unpaidPlayers || [] // Use the unpaid players data
            };
            
            console.log('🔍 PaymentPage - Created team payment with API data from TeamRegistration:', {
              playerCount: paymentData.playerCount,
              perPlayerFee: paymentData.perPlayerFee,
              totalAmount: paymentData.totalAmount,
              amountPaid: paymentData.amountPaid,
              minimumAmountToPay: paymentData.minimumAmountToPay,
              balanceAmount: paymentData.balanceAmount
            });
            setTeamPayments([teamPayment]);
            setSelectedPlayers(location.state.unpaidPlayers || []);
          } else {
            console.log('PaymentPage - Payment data has null values, using fallback calculations');
            // Fallback to calculated amounts when API returns null
            const fallbackPlayerFee = 1000; // Default fee
            const fallbackTotalAmount = paymentData.playerCount * fallbackPlayerFee;
            
            const teamPayment: TeamPayment = {
              _id: team._id,
              name: team.name,
              logo: team.logo,
              ageGroup: team.ageGroups.join(', '),
              playersCount: paymentData.playerCount,
              playerFee: fallbackPlayerFee,
              firstPayment: fallbackTotalAmount * 0.25, // 25% as first payment
              amountPending: paymentData.amountPaid || 0, // Use amount already paid if available
              revisedAmount: fallbackTotalAmount, // Calculated total amount
              secondPayment: fallbackTotalAmount * 0.75, // 75% as second payment
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: location.state.unpaidPlayers || [] // Use the unpaid players data
            };
            
            setTeamPayments([teamPayment]);
            setSelectedPlayers(location.state.unpaidPlayers || []);
          }
        } else {
          console.log('🔍 PaymentPage - No payment preview data found, checking for unpaid players');
          
          if (location.state?.unpaidPlayers && location.state.unpaidPlayers.length > 0) {
            console.log('PaymentPage - Processing unpaid players (fallback):', location.state.unpaidPlayers);
            
            // Get the team from selected teams
            const team = location.state.selectedTeams[0];
            
            // Create team payment with only unpaid players (fallback to mock data)
            const teamPayment: TeamPayment = {
              _id: team._id,
              name: team.name,
              logo: team.logo,
              ageGroup: team.ageGroups.join(', '),
              playersCount: location.state.unpaidPlayers.length,
              playerFee: 1000, // Default fee
              firstPayment: location.state.unpaidPlayers.length * 1000 * 0.25, // 25% of total
              amountPending: 0, // No pending amount for new registrations
              revisedAmount: location.state.unpaidPlayers.length * 1000,
              secondPayment: location.state.unpaidPlayers.length * 1000 * 0.75, // 75% remaining
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: location.state.unpaidPlayers
            };
            
            setTeamPayments([teamPayment]);
            setSelectedPlayers(location.state.unpaidPlayers);
          } else {
            console.log('PaymentPage - No unpaid players found, using fallback mock data');
            
            // Convert selected teams to team payments format (fallback to mock data)
            const teamsFromRegister = location.state.selectedTeams.map((team: SelectedTeam) => ({
              _id: team._id,
              name: team.name,
              logo: team.logo,
              ageGroup: team.ageGroups.join(', '),
              playersCount: team.players || 0,
              playerFee: 1000, // Default fee
              firstPayment: (team.players || 0) * 1000 * 0.25, // 25% of total
              amountPending: 0, // No pending amount for new registrations
              revisedAmount: (team.players || 0) * 1000,
              secondPayment: (team.players || 0) * 1000 * 0.75, // 75% remaining
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: [] // Empty array for register flow
            }));
            
            console.log('PaymentPage - Converted teams for payment:', teamsFromRegister);
            setTeamPayments(teamsFromRegister);
          }
        }
      } else if (location.state?.paymentPreview) {
        // Check if we have payment preview data from PlayerManagement - PRIORITY 2
        console.log('PaymentPage - Using real payment preview data from PlayerManagement:', location.state.paymentPreview);
        setPaymentPreviewData(location.state.paymentPreview);
        setPaymentSource('playerManagement');
        
        // DEBUG: Log the API data structure
        console.log('🔍 PaymentPage - API Data Structure:', {
          playerCount: location.state.paymentPreview.playerCount,
          perPlayerFee: location.state.paymentPreview.perPlayerFee,
          totalAmount: location.state.paymentPreview.totalAmount,
          amountPaid: location.state.paymentPreview.amountPaid,
          minimumAmountToPay: location.state.paymentPreview.minimumAmountToPay,
          balanceAmount: location.state.paymentPreview.balanceAmount
        });
        
        // Handle selected players from PlayerManagement with real payment data
        if (location.state?.selectedPlayers && location.state?.team) {
          const selectedPlayers = location.state.selectedPlayers;
          const team = location.state.team;
          const paymentData = location.state.paymentPreview;
          
          console.log('🔍 PaymentPage - Processing real payment data from PlayerManagement:', {
            selectedPlayersCount: selectedPlayers.length,
            teamName: team.name,
            paymentData: paymentData
          });
          
          setSelectedPlayers(selectedPlayers);
          
          // Validate payment data and use fallbacks for null/undefined values (removed fullAmount check)
          const hasValidPaymentData = paymentData.totalAmount && 
                                    paymentData.perPlayerFee;
          
          console.log('🔍 PaymentPage - Has valid payment data:', hasValidPaymentData);
          
          if (hasValidPaymentData) {
            // Create team payment with real payment data from API
            const teamPayment: TeamPayment = {
              _id: team._id,
              name: team.name,
              logo: team.logo,
              ageGroup: team.ageGroups.join(', '),
              playersCount: paymentData.playerCount, // Use API playerCount
              playerFee: paymentData.perPlayerFee, // Use API perPlayerFee
              firstPayment: paymentData.minimumAmountToPay, // Use API minimumAmountToPay
              amountPending: paymentData.amountPaid || 0, // Use API amountPaid
              revisedAmount: paymentData.totalAmount, // Use API totalAmount
              secondPayment: paymentData.balanceAmount, // Use API balanceAmount
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: selectedPlayers
            };
            
            console.log('🔍 PaymentPage - Created team payment with API data from PlayerManagement:', {
              playerCount: paymentData.playerCount,
              perPlayerFee: paymentData.perPlayerFee,
              totalAmount: paymentData.totalAmount,
              amountPaid: paymentData.amountPaid,
              minimumAmountToPay: paymentData.minimumAmountToPay,
              balanceAmount: paymentData.balanceAmount
            });
            setTeamPayments([teamPayment]);
          } else {
            console.log('🔍 PaymentPage - Payment data has null values, using fallback calculations');
            // Fallback to calculated amounts when API returns null
            const fallbackPlayerFee = 1000; // Default fee
            const fallbackTotalAmount = paymentData.playerCount * fallbackPlayerFee;
            
            const teamPayment: TeamPayment = {
              _id: team._id,
              name: team.name,
              logo: team.logo,
              ageGroup: team.ageGroups.join(', '),
              playersCount: paymentData.playerCount,
              playerFee: fallbackPlayerFee,
              firstPayment: fallbackTotalAmount * 0.25, // 25% as first payment
              amountPending: paymentData.amountPaid || 0, // Use amount already paid if available
              revisedAmount: fallbackTotalAmount, // Calculated total amount
              secondPayment: fallbackTotalAmount * 0.75, // 75% as second payment
              dueDate: '30 July 2025',
              paymentOption: 'total' as const,
              customAmount: undefined,
              players: selectedPlayers
            };
            
            console.log('🔍 PaymentPage - Created team payment with fallback data:', teamPayment);
            setTeamPayments([teamPayment]);
          }
        }
      } else if (location.state?.source === 'playerManagement') {
        console.log('PaymentPage - Loading from PlayerManagement flow (fallback)');
        setPaymentSource('playerManagement');
        
        // Handle selected players from PlayerManagement (fallback without payment preview)
        if (location.state?.selectedPlayers && location.state?.team) {
          const selectedPlayers = location.state.selectedPlayers;
          const team = location.state.team;
          
          setSelectedPlayers(selectedPlayers);
          
          // Create team payment with selected players (fallback to mock data)
          const teamPayment: TeamPayment = {
            _id: team._id,
            name: team.name,
            logo: team.logo,
            ageGroup: team.ageGroups.join(', '),
            playersCount: selectedPlayers.length,
            playerFee: 1000, // Default fee, can be made dynamic
            firstPayment: selectedPlayers.length * 1000 * 0.25, // 25% of total
            amountPending: 0, // Set to 0 for consistency with register flow
            revisedAmount: selectedPlayers.length * 1000,
            secondPayment: selectedPlayers.length * 1000 * 0.75, // 75% remaining
            dueDate: '30 July 2025',
            paymentOption: 'total' as const,
            customAmount: undefined,
            players: selectedPlayers
          };
          
          setTeamPayments([teamPayment]);
        } else {
          // Fallback to mock data
          setTeamPayments(mockTeamPayments);
        }
      } else {
        console.log('PaymentPage - No valid data found, using fallback mock data');
        // Use existing mock data as fallback
        setPaymentSource('register');
        setTeamPayments(mockTeamPayments);
      }

      // Load tournament data dynamically
      try {
        if (location.state?.tournament) {
          setTournament(location.state.tournament);
        } else if (tournamentId) {
          const tournamentData = await getTournamentById(tournamentId);
          setTournament({
            _id: tournamentId,
            name: tournamentData.name || 'Tournament',
            logo: tournamentData.logo || sflLogo
          });
        } else {
          // Fallback tournament data
          setTournament({
            _id: tournamentId || '',
            name: 'Tournament',
            logo: sflLogo
          });
        }
      } catch (error) {
        console.error('Error loading tournament data:', error);
        // Fallback tournament data
        setTournament({
          _id: tournamentId || '',
          name: 'Tournament',
          logo: sflLogo
        });
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
    
    // Debug information
    console.log('🔍 PaymentPage - Final state after loadData:', {
      paymentSource,
      teamPayments: teamPayments.length,
      selectedTeams: selectedTeams.length,
      selectedPlayers: selectedPlayers.length,
      paymentPreviewData: !!paymentPreviewData,
      tournament: !!tournament,
      loading
    });
  };

  // Payment handlers
  const handlePaymentOptionChange = (teamId: string, option: 'total' | 'minimum') => {
    setTeamPayments(prev => prev.map(team => 
      team._id === teamId 
        ? { ...team, paymentOption: option, customAmount: undefined }
        : team
    ));
  };

  const handleCustomAmountChange = (teamId: string, amount: number) => {
    // Clear any previous errors
    setTeamPayments(prev => prev.map(team => 
      team._id === teamId 
        ? { ...team, customAmountError: undefined }
        : team
    ));

    // ✅ Consistent record payment detection
    const isRecordFlow = location.state?.isRecordPayment || isRecordPayment;
    console.log('🔍 Custom Amount Validation - isRecordFlow:', isRecordFlow, 'amount:', amount);

    // SECURITY: Enhanced validation with stricter rules
    let errorMessage = '';
    
    if (amount > 0) {
      // Check if amount is a valid number
      if (isNaN(amount) || !isFinite(amount)) {
        errorMessage = 'Please enter a valid amount';
      }
      // Check if amount is in hundreds or thousands (divisible by 100) - skip for record payment
      else if (!isRecordPayment && amount % 100 !== 0) {
        errorMessage = 'Please enter an amount in hundreds or thousands only (e.g., 100, 200, 500, 1000, 1500, etc.)';
      }
      // Check minimum amount (should be at least 100)
      else if (amount < 100) {
        errorMessage = 'Minimum amount should be ₹100';
      }
      // Check maximum amount (should not exceed total amount)
      else {
        const team = teamPayments.find(t => t._id === teamId);
        if (team) {
          if (paymentPreviewData) {
            let minAmount: number;
            let maxAmount: number;
            
            if (paymentSource === 'dues') {
              // For dues payments: use amountToPay as minimum and balanceAmount as maximum
              minAmount = paymentPreviewData.amountToPay || paymentPreviewData.minimumAmountToPay || 0;
              maxAmount = paymentPreviewData.balanceAmount || 0;
            } else if (location.state?.isRecordPayment || location.state?.source === 'teamRegistration' || location.state?.source === 'playerManagement') {
              // ✅ For record payments AND Pay Now flow: use minimumAmountToPay as minimum and balanceAmount as maximum
              minAmount = paymentPreviewData.minimumAmountToPay || 0;
              maxAmount = paymentPreviewData.balanceAmount || 0;
              console.log('🔍 Record Payment/Pay Now - Custom amount validation range:', { minAmount, maxAmount });
            } else {
              // For regular payments: use minimumAmountToPay as minimum and fullAmount as maximum
              minAmount = paymentPreviewData.minimumAmountToPay || 0;
              maxAmount = paymentPreviewData.fullAmount || paymentPreviewData.totalAmount || 0;
            }
            
            if (amount < minAmount && !isRecordFlow) {
              errorMessage = `Amount must be at least ₹${minAmount.toLocaleString()}`;
              console.log('🔍 Validation failed - amount < minAmount:', amount, '<', minAmount);
            } else if (amount > maxAmount) {
              errorMessage = `Amount cannot exceed ₹${maxAmount.toLocaleString()}`;
              console.log('🔍 Validation failed - amount > maxAmount:', amount, '>', maxAmount);
            } else if (amount !== minAmount && amount % 100 !== 0) {
              // ✅ Always enforce hundreds validation for both record payments and regular payments
              // EXCEPTION: Allow exact minimum amount even if it's not a multiple of 100
              if (isRecordFlow) {
                errorMessage = `Please enter an amount in hundreds or thousands only (e.g., 100, 200, 500, 1000, 1500, etc.)`;
              } else {
                errorMessage = `Please enter an amount in hundreds or thousands only (e.g., 100, 200, 500, 1000, 1500, etc.) or the exact minimum amount of ₹${minAmount.toLocaleString()}`;
              }
              console.log('🔍 Validation failed - not in hundreds:', amount, '% 100 =', amount % 100);
            }
          } else {
            // Fallback to team data
            const maxAmount = team.revisedAmount;
            
            if (amount > maxAmount) {
              errorMessage = `Amount cannot exceed ₹${maxAmount.toLocaleString()}`;
              console.log('🔍 Fallback validation failed - amount > maxAmount:', amount, '>', maxAmount);
            } else if (amount % 100 !== 0) {
              // ✅ Always enforce hundreds validation for both record payments and regular payments
              errorMessage = `Please enter an amount in hundreds or thousands only (e.g., 100, 200, 500, 1000, 1500, etc.)`;
              console.log('🔍 Fallback validation failed - not in hundreds:', amount, '% 100 =', amount % 100);
            }
          }
        }
      }
      
          // SECURITY: Additional validation for reasonable amounts
    if (amount > 1000000) { // 10 lakhs
      errorMessage = 'Amount seems unusually high. Please verify the amount.';
      console.log('🔍 Validation failed - amount too high:', amount);
    }
  }
  
  // ✅ Log validation result
  if (errorMessage) {
    console.log('🔍 Custom Amount Validation FAILED:', errorMessage);
  } else {
    console.log('✅ Custom Amount Validation PASSED for record payment:', amount);
  }
  
  setTeamPayments(prev => prev.map(team => 
    team._id === teamId 
      ? { 
          ...team, 
          customAmount: amount, 
          paymentOption: amount > 0 ? 'custom' : 'minimum',
          customAmountError: errorMessage,
          customAmountTouched: true
        }
      : team
  ));
  };

  const handleCustomAmountFocus = (teamId: string) => {
    setTeamPayments(prev => prev.map(team => 
      team._id === teamId 
        ? { ...team, paymentOption: 'custom' }
        : team
    ));
  };

  const handleCustomAmountBlur = (teamId: string) => {
    // Mark as touched when user leaves the field
    setTeamPayments(prev => prev.map(team => 
      team._id === teamId 
        ? { ...team, customAmountTouched: true }
        : team
    ));
  };

  // Format amount for display (add commas)
  const formatAmount = (amount: number): string => {
    return amount.toLocaleString('en-IN');
  };

  // Parse formatted amount back to number
  const parseAmount = (value: string): number => {
    return parseInt(value.replace(/,/g, '')) || 0;
  };

  // Handle formatted input change
  const handleFormattedAmountChange = (teamId: string, value: string) => {
    // Remove all non-numeric characters except commas
    const cleanValue = value.replace(/[^\d,]/g, '');
    
    // Parse the numeric value
    const numericValue = parseAmount(cleanValue);
    
    // Only update if the value is valid or empty
    if (cleanValue === '' || numericValue >= 0) {
      handleCustomAmountChange(teamId, numericValue);
    }
  };

  // Validate if custom amount is valid for payment
  const isCustomAmountValid = (team: TeamPayment): boolean => {
    if (!team.customAmount || team.customAmount <= 0) {
      return false;
    }
    
    // ✅ Consistent record payment detection
    const isRecordFlow = location.state?.isRecordPayment || isRecordPayment;
    console.log('🔍 isCustomAmountValid - isRecordFlow:', isRecordFlow, 'amount:', team.customAmount);
    
    // Check minimum amount (skip for record payments)
    if (!isRecordFlow && team.customAmount < 100) {
      console.log('🔍 isCustomAmountValid FAILED - amount < 100:', team.customAmount);
      return false;
    }
    
    // Use payment preview data for validation if available
    if (paymentPreviewData) {
      let minAmount: number;
      let maxAmount: number;
      
      if (paymentSource === 'dues') {
        // For dues payments: use amountToPay as minimum and balanceAmount as maximum
        minAmount = paymentPreviewData.amountToPay || paymentPreviewData.minimumAmountToPay || 0;
        maxAmount = paymentPreviewData.balanceAmount || 0;
      } else if (location.state?.isRecordPayment || location.state?.source === 'teamRegistration' || location.state?.source === 'playerManagement') {
        // ✅ For record payments AND Pay Now flow: use minimumAmountToPay as minimum and balanceAmount as maximum
        minAmount = paymentPreviewData.minimumAmountToPay || 0;
        maxAmount = paymentPreviewData.balanceAmount || 0;
      } else {
        // For regular payments: use minimumAmountToPay as minimum and fullAmount as maximum
        minAmount = paymentPreviewData.minimumAmountToPay || 0;
        maxAmount = paymentPreviewData.fullAmount || paymentPreviewData.totalAmount || 0;
      }
      
      if (!isRecordFlow && team.customAmount < minAmount) {
        console.log('🔍 isCustomAmountValid FAILED - amount < minAmount:', team.customAmount, '<', minAmount);
        return false;
      }
      
      if (team.customAmount > maxAmount) {
        return false;
      }
      
      // Check if amount is in hundreds or thousands (no decimal values like 1324.50)
      // EXCEPTION: Allow the exact minimum amount even if it's not a multiple of 100
      // ✅ Always enforce hundreds validation for both record payments and regular payments
      if (team.customAmount !== minAmount && team.customAmount % 100 !== 0) {
        console.log('🔍 isCustomAmountValid FAILED - not in hundreds:', team.customAmount, '% 100 =', team.customAmount % 100);
        return false;
      }
    } else {
      // Fallback to team data
      const maxAmount = team.revisedAmount;
      
      if (team.customAmount > maxAmount) {
        return false;
      }
      
      // Check if amount is in hundreds or thousands (no decimal values like 1324.50)
      // ✅ Always enforce hundreds validation for both record payments and regular payments
      if (team.customAmount % 100 !== 0) {
        console.log('🔍 isCustomAmountValid FAILED - fallback not in hundreds:', team.customAmount, '% 100 =', team.customAmount % 100);
        return false;
      }
    }
    
    console.log('✅ isCustomAmountValid PASSED for amount:', team.customAmount);
    return true;
  };

  // Check if balance amount is zero (no payment needed)
  const isBalanceZero = (): boolean => {
    if (!paymentPreviewData) {
      return false;
    }
    
    // ✅ ENHANCED: Check for balance amount 0 in any flow that has payment preview data
    // This ensures we catch cases where balance is 0 regardless of flow detection
    if (paymentPreviewData.balanceAmount !== undefined && paymentPreviewData.balanceAmount === 0) {
      return true;
    }
    
    // For record payments AND Pay Now flow, check if balance amount is 0
    const isRecordFlow = isRecordPayment || location.state?.isRecordPayment;
    const isPayNowFlow = location.state?.source === 'teamRegistration' || location.state?.source === 'playerManagement';
    
    if (isRecordFlow || isPayNowFlow) {
      return (paymentPreviewData.balanceAmount || 0) === 0;
    }
    
    return false;
  };

  // Get payment button state
  const getPaymentButtonState = (): { disabled: boolean; text: string } => {
    if (isProcessingPayment) {
      return { disabled: true, text: 'Processing Payment...' };
    }
    
    if (teamPayments.length === 0) {
      return { disabled: true, text: 'No payment data available' };
    }
    
    // Check if balance is zero (fully paid)
    if (isBalanceZero()) {
      return { disabled: true, text: 'Payment Complete - No Balance Due' };
    }
    
    const team = teamPayments[0];
    
    if (team.paymentOption === 'custom') {
      if (!isCustomAmountValid(team)) {
        return { disabled: true, text: 'Please enter a valid amount' };
      }
    }
    
    // ✅ FLOW-SPECIFIC BUTTON TEXT:
    // DUES FLOW: "Pay Dues"
    // RECORD PAYMENT FLOW: "Record Payment" 
    // PAY NOW FLOW: "Pay Securely"
    return { disabled: false, text: isRecordPayment ? 'Record Payment' : (paymentSource === 'dues' ? 'Pay Dues' : 'Pay Securely') };
  };

  // Get suggested amounts based on team data
  const getSuggestedAmounts = (team: TeamPayment): number[] => {
    const suggestions = [];
    
    // Use payment preview data for suggestions if available
    if (paymentPreviewData) {
      let minAmount: number;
      let maxAmount: number;
      
      if (paymentSource === 'dues') {
        // For dues payments: use amountToPay as minimum and balanceAmount as maximum
        minAmount = paymentPreviewData.amountToPay || paymentPreviewData.minimumAmountToPay || 0;
        maxAmount = paymentPreviewData.balanceAmount || 0;
      } else {
        // For non-dues payments: use minimumAmountToPay as minimum and fullAmount as maximum
        minAmount = paymentPreviewData.minimumAmountToPay || 0;
        maxAmount = paymentPreviewData.fullAmount || paymentPreviewData.totalAmount || 0;
      }
      
      // Add minimum amount (exact amount, not rounded)
      if (minAmount > 0) {
        suggestions.push(minAmount);
      }
      
      // For dues payments: Generate 2-3 suggested amounts between min and max
      if (paymentSource === 'dues' && maxAmount > minAmount) {
        const range = maxAmount - minAmount;
        
        // Add 1/3 of the range
        const thirdAmount = minAmount + Math.round(range / 3);
        const roundedThirdAmount = Math.round(thirdAmount / 100) * 100;
        if (roundedThirdAmount > minAmount && roundedThirdAmount < maxAmount) {
          suggestions.push(roundedThirdAmount);
        }
        
        // Add 2/3 of the range
        const twoThirdsAmount = minAmount + Math.round((range * 2) / 3);
        const roundedTwoThirdsAmount = Math.round(twoThirdsAmount / 100) * 100;
        if (roundedTwoThirdsAmount > minAmount && roundedTwoThirdsAmount < maxAmount) {
          suggestions.push(roundedTwoThirdsAmount);
        }
      } else {
        // For non-dues payments: Add half of full amount
        if (maxAmount > 0) {
          const halfAmount = Math.round(maxAmount / 2 / 100) * 100;
          if (halfAmount >= minAmount && halfAmount !== minAmount && halfAmount !== maxAmount) {
            suggestions.push(halfAmount);
          }
        }
      }
      
      // Add maximum amount (rounded to nearest hundred)
      if (maxAmount > 0 && maxAmount !== minAmount) {
        const roundedMaxAmount = Math.round(maxAmount / 100) * 100;
        suggestions.push(roundedMaxAmount);
      }
    } else {
      // Fallback to team data
      const minAmount = team.revisedAmount * 0.1; // 10% of revised amount
      const maxAmount = team.revisedAmount;
      
      // Add minimum amount
      if (minAmount > 0) {
        const roundedMinAmount = Math.round(minAmount / 100) * 100;
        suggestions.push(roundedMinAmount);
      }
      
      // Add half amount
      const halfAmount = Math.round(maxAmount / 2 / 100) * 100;
      if (halfAmount >= minAmount && halfAmount !== minAmount) {
        suggestions.push(halfAmount);
      }
      
      // Add maximum amount
      const roundedMaxAmount = Math.round(maxAmount / 100) * 100;
      suggestions.push(roundedMaxAmount);
    }
    
    // Remove duplicates and sort
    const uniqueSuggestions = Array.from(new Set(suggestions)).sort((a, b) => a - b);
    
    // Limit to 3 suggestions
    return uniqueSuggestions.slice(0, 3);
  };

  const handleProceedPayment = (teamId: string) => {
    console.log('Proceed payment for team:', teamId);
    // Handle individual team payment
  };

  const handlePayNow = async () => {
    console.log('🔍 Starting payment process...');
    
    // SECURITY: Rate limiting check
    const now = Date.now();
    if (now - lastPaymentAttempt < PAYMENT_COOLDOWN_MS) {
      const remainingTime = Math.ceil((PAYMENT_COOLDOWN_MS - (now - lastPaymentAttempt)) / 1000);
      setPaymentError(`Please wait ${remainingTime} seconds before trying again.`);
      return;
    }
    setLastPaymentAttempt(now);
    
    if (teamPayments.length === 0) {
      setPaymentError('No payment data available');
      return;
    }

    const team = teamPayments[0];
    
    // Validate payment amount
    if (team.paymentOption === 'custom' && !isCustomAmountValid(team)) {
      setPaymentError('Please enter a valid payment amount');
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError(null);

    try {
      // Validate required data
      if (!tournamentId) {
        throw new Error('Tournament ID is required');
      }
      
      if (!team._id) {
        throw new Error('Team ID is required');
      }
      
      // Skip player selection validation for dues payments and record payments
      // For record payments, we use allPlayers from location.state instead of selectedPlayers
      const isRecordPaymentFlow = location.state?.isRecordPayment;
      const hasAllPlayersData = location.state?.allPlayers && location.state.allPlayers.length > 0;
      
      // ✅ FLOW-SPECIFIC VALIDATION:
      // - DUES FLOW: Skip player validation (dues payments don't require player selection)
      // - PAY NOW FLOW: Require player selection (team registration/player management)
      // - RECORD PAYMENT FLOW: Skip player validation (uses existing registration)
      if (paymentSource !== 'dues' && !isRecordPaymentFlow && selectedPlayers.length === 0) {
        throw new Error('No players selected for registration');
      }
      
      // For record payment flow, validate allPlayers data
      if (isRecordPaymentFlow && !hasAllPlayersData) {
        throw new Error('No players found for record payment');
      }
      
      // Calculate the exact amount based on user's payment option
      let amountToPay: number;
      
      if (paymentPreviewData) {
        if (team.paymentOption === 'total') {
          // ✅ FLOW-SPECIFIC AMOUNT CALCULATION - PAY TOTAL:
          // DUES FLOW: Use balanceAmount (remaining amount to pay)
          // PAY NOW FLOW: Use balanceAmount (remaining amount to pay)
          // RECORD PAYMENT FLOW: Use balanceAmount (remaining amount to pay)
          // REGISTER FLOW: Use fullAmount (total tournament fee)
          if (paymentSource === 'dues') {
            // For dues payments: use balanceAmount as the total amount to pay
            amountToPay = paymentPreviewData.balanceAmount || 0;
          } else if (location.state?.isRecordPayment || location.state?.source === 'teamRegistration' || location.state?.source === 'playerManagement') {
            // ✅ For record payments AND Pay Now flow: use balanceAmount since user has already paid some amount
            amountToPay = paymentPreviewData.balanceAmount || 0;
            console.log('🔍 Record Payment/Pay Now - Using balanceAmount for Pay Total:', amountToPay);
          } else {
            // For regular payments: use fullAmount as the total amount to pay
            amountToPay = paymentPreviewData.fullAmount || paymentPreviewData.totalAmount || 0;
          }
        } else if (team.paymentOption === 'custom' && team.customAmount && team.customAmount > 0) {
          // Enter another amount: Use user's custom amount
          amountToPay = team.customAmount;
        } else {
          if (paymentSource === 'dues') {
            // For dues payments: use amountToPay as the minimum amount to pay
            amountToPay = paymentPreviewData.amountToPay || paymentPreviewData.minimumAmountToPay || 0;
          } else {
            // For non-dues payments: use minimumAmountToPay as the minimum amount to pay
            amountToPay = paymentPreviewData.minimumAmountToPay || 0;
          }
        }
      } else {
        // Fallback to existing calculation logic
        if (team.paymentOption === 'total') {
          amountToPay = team.revisedAmount || 0;
        } else if (team.paymentOption === 'custom' && team.customAmount && team.customAmount > 0) {
          amountToPay = team.customAmount;
        } else {
          // Pay minimum - use exact fallback amount
          amountToPay = team.firstPayment || 0;
        }
      }
      
      // Validate amount
      if (amountToPay <= 0) {
        throw new Error('Invalid payment amount. Please select a valid payment option.');
      }
      
      // SECURITY: Server-side amount validation
      if (paymentPreviewData) {
        let maxAllowedAmount: number;
        let minAllowedAmount: number;
        
        if (paymentSource === 'dues') {
          // For dues payments: use balanceAmount as maximum and amountToPay as minimum
          maxAllowedAmount = paymentPreviewData.balanceAmount || 0;
          minAllowedAmount = paymentPreviewData.amountToPay || paymentPreviewData.minimumAmountToPay || 0;
        } else if (location.state?.isRecordPayment || location.state?.source === 'teamRegistration' || location.state?.source === 'playerManagement') {
          // ✅ For record payments AND Pay Now flow: use balanceAmount as maximum and minimumAmountToPay as minimum
          maxAllowedAmount = paymentPreviewData.balanceAmount || 0;
          minAllowedAmount = paymentPreviewData.minimumAmountToPay || 0;
        } else {
          // For regular payments: use fullAmount as maximum and minimumAmountToPay as minimum
          maxAllowedAmount = paymentPreviewData.fullAmount || paymentPreviewData.totalAmount || 0;
          minAllowedAmount = paymentPreviewData.minimumAmountToPay || 0;
        }
        
        if (amountToPay > maxAllowedAmount) {
          throw new Error('Payment amount exceeds maximum allowed amount');
        }
        
        // Only validate minimum amount for non-custom payments
        if (team.paymentOption !== 'custom' && amountToPay < minAllowedAmount) {
          throw new Error('Payment amount is below minimum required amount');
        }
        
        // For custom amounts, validate that it's between minimum and maximum
        if (team.paymentOption === 'custom') {
          // ✅ Skip minimum amount validation for record payments
          const isRecordFlow = location.state?.isRecordPayment || isRecordPayment;
          console.log('🔍 handlePayNow - Custom amount validation - isRecordFlow:', isRecordFlow, 'amount:', amountToPay, 'minAllowed:', minAllowedAmount);
          if (!isRecordFlow && amountToPay < minAllowedAmount) {
            console.log('🔍 handlePayNow validation failed - amount < minAllowedAmount:', amountToPay, '<', minAllowedAmount);
            throw new Error(`Payment amount must be at least ₹${minAllowedAmount.toLocaleString()}`);
          } else if (isRecordFlow && amountToPay < minAllowedAmount) {
            console.log('✅ handlePayNow - Record payment allowed below minimum:', amountToPay, '<', minAllowedAmount);
          }
          if (amountToPay > maxAllowedAmount) {
            throw new Error(`Payment amount cannot exceed ₹${maxAllowedAmount.toLocaleString()}`);
          }
          // Validate amount is in hundreds
          if (amountToPay % 100 !== 0) {
            throw new Error('Payment amount must be in hundreds');
          }
        }
      }
      
      console.log('🔍 Payment flow - Starting with team:', team);
      console.log('🔍 Payment flow - Payment source:', paymentSource);
      console.log('🔍 Payment flow - Selected players count:', selectedPlayers.length);
      console.log('🔍 Payment flow - Tournament ID:', tournamentId);
      console.log('🔍 Payment flow - Location state:', {
        hasExistingRegistration: location.state?.hasExistingRegistration,
        registrationId: location.state?.registrationId,
        paymentPreview: location.state?.paymentPreview,
        unpaidPlayerIds: location.state?.unpaidPlayerIds,
        isRecordPayment: location.state?.isRecordPayment,
        paymentMode: location.state?.paymentMode
      });

      // Step 1: Prepare registration data
      const ageCategory = team.ageGroup || 'U10'; // Default fallback
      
      // ✅ FLOW-SPECIFIC PLAYER ID RESOLUTION:
      // DUES FLOW: Use player IDs from dues data (no resolution needed)
      // PAY NOW FLOW: Resolve player IDs from navigation state
      // RECORD PAYMENT FLOW: Resolve player IDs from navigation state
      let playerIds;
      const isRecordFlow = location.state?.isRecordPayment;
      const isPayNowFlow = location.state?.source === 'teamRegistration' || location.state?.source === 'playerManagement';
      
      if (paymentSource === 'dues') {
        // ✅ DUES FLOW: Use player IDs directly from dues data
        playerIds = location.state?.due?.playerIds || [];
        console.log('🔍 Payment flow - Dues flow: Using player IDs from dues data:', playerIds);
        console.log('🔍 Payment flow - Dues flow: Player count from dues:', location.state?.due?.playerCount);
      } else {
        // ✅ PAY NOW & RECORD PAYMENT FLOWS: Resolve player IDs from navigation state
        const { allPlayers, selectedPlayers: stateSelectedPlayers, unpaidPlayerIds } = location.state;
        
        // Priority order: allPlayers > selectedPlayers > unpaidPlayerIds
        if (allPlayers && allPlayers.length > 0) {
          playerIds = allPlayers;
          console.log('🔍 Payment flow - Using allPlayers:', playerIds);
        } else if (stateSelectedPlayers && stateSelectedPlayers.length > 0) {
          playerIds = stateSelectedPlayers.map((player: any) => player._id);
          console.log('🔍 Payment flow - Using stateSelectedPlayers:', playerIds);
        } else if (unpaidPlayerIds && unpaidPlayerIds.length > 0) {
          playerIds = unpaidPlayerIds;
          console.log('🔍 Payment flow - Using unpaidPlayerIds:', playerIds);
        } else if (selectedPlayers && selectedPlayers.length > 0) {
          // Fallback to component state selectedPlayers
          playerIds = selectedPlayers.map(player => player._id);
          console.log('🔍 Payment flow - Using component selectedPlayers:', playerIds);
        } else {
          throw new Error('No player data found for payment');
        }
      }

      console.log('🔍 Payment flow - Registration data:', {
        ageCategory,
        playerIdsCount: playerIds.length,
        amountToPay,
        paymentOption: team.paymentOption,
        tournamentId,
        teamId: team._id,
        paymentPreviewData: paymentPreviewData ? {
          totalAmount: paymentPreviewData.totalAmount,
          minimumAmountToPay: paymentPreviewData.minimumAmountToPay,
          amountPaid: paymentPreviewData.amountPaid,
          balanceAmount: paymentPreviewData.balanceAmount
        } : null,
        teamPaymentData: {
          revisedAmount: team.revisedAmount,
          firstPayment: team.firstPayment,
          customAmount: team.customAmount
        }
      });

      // Step 2: Handle registration based on existing registration status
      let registrationId: string;
      let milestonePaymentId: string = '';
      let milestoneAmount: number = 0;
      
      // ✅ FLOW-SPECIFIC PROCESSING - STEP 2:
      // DUES FLOW: Skip player registration, use existing registration ID
      // PAY NOW FLOW: Process player registration via addTournamentRegistration API
      // RECORD PAYMENT FLOW: Process player registration via addTournamentRegistration API
      if (paymentSource === 'dues') {
        console.log('🔍 Payment flow - Step 2: Processing dues payment...');
        console.log('🔍 Payment flow - Using registration ID from dues:', location.state.registrationId);
        
        // For dues payments, use the existing registration ID
        registrationId = location.state.registrationId;
        
        // For dues payments, we need to get milestone payment details from payment preview
        if (!paymentPreviewData) {
          throw new Error('Payment preview data is required for dues payments');
        }
        
        // For dues payments, milestone payment details will be set in Step 3
        // after calling the milestone payment API
        console.log('🔍 Payment flow - Milestone payment details will be set in Step 3 for dues');
      }
      // Check if team has existing registration
      else if (location.state?.hasExistingRegistration && location.state?.registrationId) {
        console.log("🔍 Payment flow - Step 2: Team has existing registration, using existing registration");
        console.log("🔍 Payment flow - Using existing registration ID:", location.state.registrationId);
        console.log("🔍 Payment flow - Unpaid player IDs:", location.state?.unpaidPlayerIds);
        console.log("🔍 Payment flow - Payment preview data for existing registration:", paymentPreviewData);
        console.log("🔍 Payment flow - Payment preview full response structure:", {
          hasData: !!paymentPreviewData,
          dataKeys: paymentPreviewData ? Object.keys(paymentPreviewData) : [],
          fullResponse: paymentPreviewData
        });
        
        // ✅ UNIFIED: For record payment flow AND Pay Now flow, use addTournamentRegistration API
        if (isRecordFlow || isPayNowFlow) {
          console.log("🔍 Payment flow - Record payment or Pay Now flow detected, using addTournamentRegistration API");
          
          const requestData: any = {
            registrationId: location.state.registrationId || null, // Include existing registration ID or null
            ageCategory,
            players: playerIds,
            amountPaid: amountToPay,
            paymentMode
          };
          
          // Add payment method for offline payments
          if (paymentMode === "offline") {
            requestData.method = paymentMethod;
          }
          
          console.log("🔍 Payment flow - Record payment request data:", requestData);
          
          const registrationResponse = await addTournamentRegistration(
            tournamentId!,
            team._id,
            requestData
          );
          
          console.log("🔍 Payment flow - Record payment registration response received");
          console.log("🔍 Payment flow - Record payment response data breakdown:", {
            registration: {
              _id: registrationResponse.data?.registration?._id,
              amountPaid: registrationResponse.data?.registration?.amountPaid,
              totalAmount: registrationResponse.data?.registration?.totalAmount,
              balanceAmount: registrationResponse.data?.registration?.balanceAmount,
              isAmountPaidInFull: registrationResponse.data?.registration?.isAmountPaidInFull
            },
            milestonePayment: {
              _id: registrationResponse.data?.milestonePayment?._id,
              amount: registrationResponse.data?.milestonePayment?.amount,
              type: registrationResponse.data?.milestonePayment?.type,
              paidStatus: registrationResponse.data?.milestonePayment?.paidStatus,
              playersCount: registrationResponse.data?.milestonePayment?.players?.length
            }
          });
          
          if (!registrationResponse.data?.registration?._id || !registrationResponse.data?.milestonePayment?._id) {
            throw new Error("Invalid registration response - missing IDs");
          }
          
          registrationId = registrationResponse.data.registration._id;
          milestonePaymentId = registrationResponse.data.milestonePayment._id;
          milestoneAmount = registrationResponse.data.milestonePayment.amount;
          
          if (!milestoneAmount || milestoneAmount <= 0) {
            throw new Error("Invalid milestone payment amount received from registration API");
          }
          
          console.log("🔍 Payment flow - Record payment milestone details:", {
            milestoneAmount: registrationResponse.data.milestonePayment.amount,
            milestoneType: registrationResponse.data.milestonePayment.type,
            milestoneStatus: registrationResponse.data.milestonePayment.paidStatus,
            playersCount: registrationResponse.data.milestonePayment.players?.length
          });
        } else {
          // For non-record payment flows, use existing logic with addPlayersMidSeason
          console.log("🔍 Payment flow - Non-record payment flow, will use addPlayersMidSeason API");
          
          // For teams with existing registration, use the existing registration ID
          // The payment preview data should contain milestone information
          registrationId = location.state.registrationId;
          
          // For existing registrations, we need to get milestone payment details from payment preview
          // The payment preview API for new players should return milestone information
          if (!paymentPreviewData) {
            throw new Error("Payment preview data is required for existing registrations");
          }
          
          // For existing registrations, milestone payment details will be set in Step 3
          // after calling the mid-season API
          console.log("🔍 Payment flow - Milestone payment details will be set in Step 3");
          console.log("🔍 Payment flow - Will call addPlayersMidSeason API in Step 3");
        }        console.log('🔍 Payment flow - Will call addPlayersMidSeason API in Step 3');
      } else {
        console.log('🔍 Payment flow - Step 2: Adding new tournament registration...');
        // ✅ FIX: Include registrationId and method for record payments
        const requestData: any = {
            ageCategory,
            players: playerIds,
            amountPaid: amountToPay,
            paymentMode
        };
        
        // Add additional fields for record payment flow AND Pay Now flow
        if (isRecordFlow || isPayNowFlow) {
          requestData.registrationId = location.state?.hasExistingRegistration ? location.state?.registrationId : null;
          if (paymentMode === 'offline') {
            requestData.method = paymentMethod; // Use the selected payment method from state
          }
        }
        
        console.log('🔍 Payment flow - Final request data:', requestData);
        
        const registrationResponse = await addTournamentRegistration(
          tournamentId!,
          team._id,
          requestData
        );

        console.log('🔍 Payment flow - Registration response received');
        console.log('🔍 Payment flow - Registration data breakdown:', {
          registration: {
            _id: registrationResponse.data?.registration?._id,
            amountPaid: registrationResponse.data?.registration?.amountPaid,
            totalAmount: registrationResponse.data?.registration?.totalAmount,
            balanceAmount: registrationResponse.data?.registration?.balanceAmount,
            isAmountPaidInFull: registrationResponse.data?.registration?.isAmountPaidInFull
          },
          milestonePayment: {
            _id: registrationResponse.data?.milestonePayment?._id,
            amount: registrationResponse.data?.milestonePayment?.amount,
            type: registrationResponse.data?.milestonePayment?.type,
            paidStatus: registrationResponse.data?.milestonePayment?.paidStatus,
            playersCount: registrationResponse.data?.milestonePayment?.players?.length
          }
        });

        if (!registrationResponse.data?.registration?._id || !registrationResponse.data?.milestonePayment?._id) {
          throw new Error('Invalid registration response - missing IDs');
        }

        registrationId = registrationResponse.data.registration._id;
        milestonePaymentId = registrationResponse.data.milestonePayment._id;
        
        // The milestone payment amount is the calculated amount that the order API expects
        // This is different from amountPaid which is what we sent to the registration API
        milestoneAmount = registrationResponse.data.milestonePayment.amount;
        
        if (!milestoneAmount || milestoneAmount <= 0) {
          throw new Error('Invalid milestone payment amount received from registration API');
        }
        
        // Log the milestone payment details for debugging
        console.log('🔍 Payment flow - Milestone payment details:', {
          milestoneAmount: registrationResponse.data.milestonePayment.amount,
          milestoneType: registrationResponse.data.milestonePayment.type,
          milestoneStatus: registrationResponse.data.milestonePayment.paidStatus,
          playersCount: registrationResponse.data.milestonePayment.players?.length
        });
      }

      console.log('🔍 Payment flow - Extracted IDs and amounts:', {
        registrationId,
        milestonePaymentId,
        amountPaidToRegistration: amountToPay,
        milestonePaymentAmount: milestoneAmount,
        hasExistingRegistration: location.state?.hasExistingRegistration,
        existingRegistrationId: location.state?.registrationId
      });

      // ✅ FLOW-SPECIFIC PROCESSING - STEP 3:
      // DUES FLOW: Call makeMilestonePayment API (no player processing needed)
      // PAY NOW FLOW: Call addPlayersMidSeason API (for existing registrations)
      // RECORD PAYMENT FLOW: Skip addPlayersMidSeason (uses addTournamentRegistration from Step 2)
      if (paymentSource === 'dues') {
        console.log('🔍 Payment flow - Step 3: Processing dues payment...');
        console.log('🔍 Payment flow - Dues payment request data:', {
          registrationId: location.state.registrationId,
          amountPaid: amountToPay
        });
        
        try {
          // For dues payments, we need to make a milestone payment
          const { makeMilestonePayment } = await import('../../api/paymentTransactions/dues');
          
          console.log('🔍 Payment flow - Dues milestone payment API call details:', {
            registrationId: location.state.registrationId,
            amountPaid: amountToPay,
            registrationIdType: typeof location.state.registrationId,
            amountPaidType: typeof amountToPay
          });
          
          const milestoneResponse = await makeMilestonePayment(
            location.state.registrationId,
            amountToPay
          );
          
          console.log('🔍 Payment flow - Dues milestone payment response received:', milestoneResponse);
          console.log('🔍 Payment flow - Dues milestone response structure:', {
            hasData: !!milestoneResponse.data,
            dataKeys: milestoneResponse.data ? Object.keys(milestoneResponse.data) : [],
            hasMilestonePayment: !!milestoneResponse.data?.milestonePayment,
            hasRegistration: !!milestoneResponse.data?.registration
          });
          
          // Extract milestone payment details from the response
          if (milestoneResponse.data?.milestonePayment?._id && 
              milestoneResponse.data?.registration?._id && 
              milestoneResponse.data?.milestonePayment?.amount) {
            milestonePaymentId = milestoneResponse.data.milestonePayment._id;
            registrationId = milestoneResponse.data.registration._id;
            milestoneAmount = milestoneResponse.data.milestonePayment.amount;
            
            console.log('🔍 Payment flow - Got all required data from dues milestone response:', {
              milestonePaymentId,
              registrationId,
              milestoneAmount
            });
          } else {
            console.error('🔍 Payment flow - Dues milestone API response missing required fields:', {
              hasMilestonePayment: !!milestoneResponse.data?.milestonePayment,
              hasRegistration: !!milestoneResponse.data?.registration,
              hasAmount: !!milestoneResponse.data?.milestonePayment?.amount,
              responseData: milestoneResponse.data
            });
            throw new Error('Dues milestone API did not return valid milestone payment and registration data');
          }
        } catch (error: any) {
          console.error('🔍 Payment flow - Error processing dues payment:', error);
          console.error('🔍 Payment flow - Error details:', {
            message: error.message,
            response: error.response?.data,
            status: error.response?.status,
            registrationId: location.state.registrationId,
            amountPaid: amountToPay
          });
          throw new Error(`Failed to process dues payment: ${error.message}`);
        }
      } else if (location.state?.hasExistingRegistration && location.state?.registrationId && !isRecordFlow) {        console.log('🔍 Payment flow - Step 3: Adding players mid-season for existing registration...');
        // ✅ FIX: Skip addPlayersMidSeason for record payment flows - they use addTournamentRegistration instead
        console.log("🔍 Payment flow - Step 3: Skipping addPlayersMidSeason for record payment flow");
      } else if (location.state?.hasExistingRegistration && location.state?.registrationId && !isRecordFlow) {        console.log('🔍 Payment flow - Mid-season request data:', {
          registrationId: location.state.registrationId,
          unpaidPlayerIds: location.state.unpaidPlayerIds,
          selectedPlayers: playerIds, // ✅ UNIFIED: Use the unified playerIds
          finalPlayerIds: playerIds, // ✅ UNIFIED: Use the unified playerIds
          amountToPay,
          paymentMode
        });
        
        try {
          // Determine payment type based on user's selection
          let paymentType: 'full_amount' | 'min_amount' | 'custom_amount';
          if (team.paymentOption === 'total') {
            paymentType = 'full_amount';
          } else if (team.paymentOption === 'minimum') {
            paymentType = 'min_amount';
          } else if (team.paymentOption === 'custom') {
            // For custom amounts, send custom_amount as payment type
            paymentType = 'custom_amount';
          } else {
            // Fallback logic for any other cases
            if (paymentPreviewData) {
              const fullAmount = paymentPreviewData.totalAmount || 0;
              const minAmount = paymentPreviewData.minimumAmountToPay || 0;
              
              // If custom amount equals or exceeds full amount, treat as full payment
              if (amountToPay >= fullAmount) {
                paymentType = 'full_amount';
              } else {
                paymentType = 'min_amount';
              }
            } else {
              // Fallback to minimum for custom amounts when no payment preview data
              paymentType = 'min_amount';
            }
          }
          
          console.log('🔍 Payment flow - Payment type determined:', {
            paymentOption: team.paymentOption,
            amountToPay,
            paymentType,
            fullAmount: paymentPreviewData?.totalAmount,
            minAmount: paymentPreviewData?.minimumAmountToPay
          });
          
          console.log('🔍 Payment flow - Final mid-season API call parameters:', {
            registrationId: location.state.registrationId,
            playerIds: playerIds, // ✅ UNIFIED: Use the unified playerIds
            amountToPay,
            paymentType,
            paymentMode
          });
          
          const midSeasonResponse = await addPlayersMidSeason(
            location.state.registrationId,
            playerIds, // ✅ UNIFIED: Use the unified playerIds
            amountToPay,
            paymentType,
            paymentMode
          );
          
          console.log('🔍 Payment flow - Mid-season response received:', midSeasonResponse);
          console.log('🔍 Payment flow - Mid-season response structure:', {
            hasData: !!midSeasonResponse.data,
            dataKeys: midSeasonResponse.data ? Object.keys(midSeasonResponse.data) : [],
            hasId: !!midSeasonResponse.data?._id,
            hasRegistration: !!midSeasonResponse.data?.registration,
            hasAmount: !!midSeasonResponse.data?.amount
          });
          console.log('🔍 Payment flow - Mid-season full response:', midSeasonResponse);
          
          // Extract all required fields directly from the mid-season response
          if (midSeasonResponse.data?._id && midSeasonResponse.data?.registration && midSeasonResponse.data?.amount) {
            milestonePaymentId = midSeasonResponse.data._id;
            registrationId = midSeasonResponse.data.registration;
            milestoneAmount = midSeasonResponse.data.amount;
            
            console.log('🔍 Payment flow - Got all required data from mid-season response:', {
              milestonePaymentId,
              registrationId,
              milestoneAmount
            });
          } else {
            console.error('🔍 Payment flow - Mid-season API response missing required fields:', {
              hasId: !!midSeasonResponse.data?._id,
              hasRegistration: !!midSeasonResponse.data?.registration,
              hasAmount: !!midSeasonResponse.data?.amount,
              responseData: midSeasonResponse.data
            });
            throw new Error('Mid-season API did not return valid _id, registration, and amount');
          }
        } catch (error: any) {
          console.error('🔍 Payment flow - Error adding players mid-season:', error);
          throw new Error(`Failed to add players to existing registration: ${error.message}`);
        }
      }

      // Validate that milestone payment details are set after Step 3
      if (!milestonePaymentId || milestoneAmount <= 0) {
        console.error('🔍 Payment flow - Invalid milestone payment details:', {
          milestonePaymentId,
          milestoneAmount,
          hasExistingRegistration: location.state?.hasExistingRegistration,
          paymentSource
        });
        throw new Error('Invalid milestone payment details. Please try again.');
      }

      // Step 4: Handle record payment mode (offline) or create payment order for Razorpay (online)
      if (isRecordPayment) {
        console.log('🔍 Payment flow - Step 4: Record payment mode detected, skipping Razorpay order creation');
        
        // For record payment mode, we've already made the API calls above
        // Show success popup and navigate to success page
        console.log('🔍 Payment flow - Record payment successful, showing success popup');
        setShowSuccessPopup(true);
        
        // Auto-hide popup and navigate after 2 seconds
        setTimeout(() => {
          setShowSuccessPopup(false);
          navigate(`/registered-tournaments/${tournamentId}`);
        }, 2000);
        
        return;
      }
      
      // Step 4: Create payment order for Razorpay (online mode)
      console.log('🔍 Payment flow - Step 4: Creating payment order for online payment...');
      
      // Use the user's custom amount for order when payment option is custom; otherwise use backend milestone amount
      const orderAmount = team.paymentOption === 'custom' ? amountToPay : milestoneAmount;
      
      console.log('🔍 Payment flow - Order request data:', {
        milestonePaymentId,                  // From mid-season API (_id)
        registration: registrationId,        // From mid-season API (registration)
        moduleType: "tournament-registration", // Fixed value
        amountPaid: orderAmount,             // Charge amount for order
        amountPaidToRegistration: amountToPay,
        paymentOption: team.paymentOption
      });
      
      const orderResponse = await createTournamentPaymentOrder({
        milestonePaymentId,                  // From mid-season API (_id)
        registration: registrationId,        // From mid-season API (registration)
        moduleType: "tournament-registration", // Fixed value
        amountPaid: orderAmount              // Charge amount for order
      });

      console.log('🔍 Payment flow - Order response received');
      console.log('🔍 Payment flow - Amount comparison:', {
        amountPaidToRegistration: amountToPay,
        milestonePaymentAmount: milestoneAmount,
        orderResponseAmount: orderResponse.data?.amount,
        differenceFromPaidToMilestone: Math.abs(amountToPay - milestoneAmount),
        hasExistingRegistration: location.state?.hasExistingRegistration
      });

      if (!orderResponse.data?.orderId) {
        throw new Error('Invalid order response - missing order ID');
      }

      // Step 5: Initiate Razorpay payment
      console.log('🔍 Payment flow - Step 5: Initiating Razorpay payment...');
      
      // Get Razorpay key from order response
      const razorpayKey = orderResponse.data.keyId;
      if (!razorpayKey) {
        throw new Error('Razorpay key not received from payment order. Please contact support.');
      }
      
      // Prepare user details for Razorpay
      // SECURITY: Sanitize user inputs to prevent XSS
      const sanitizeInput = (input: string | undefined): string => {
        if (!input) return '';
        // Remove any HTML tags and limit length
        return input.replace(/<[^>]*>/g, '').substring(0, 100).trim();
      };
      
      const userDetails = {
        name: sanitizeInput(user?.name || team.name),
        email: sanitizeInput(user?.email || 'user@example.com'),
        phone: sanitizeInput(user?.phone || user?.mobile || '1234567890')
      };

      console.log('🔍 Payment flow - Razorpay parameters:', {
        amount: milestoneAmount,
        orderId: orderResponse.data.orderId,
        description: `Payment for ${tournament?.name || 'Tournament'} - ${team.name}`,
        userDetails: {
          name: userDetails.name,
          email: userDetails.email,
          phone: userDetails.phone
        },
        keyIdReceived: !!razorpayKey,
        userFromRedux: {
          name: user?.name,
          email: user?.email,
          phone: user?.phone,
          mobile: user?.mobile
        }
      });
      
      try {
        await initiatePaymentWithKey(
          orderAmount, // Use the amount used to create order
          orderResponse.data.orderId,
          `Payment for ${tournament?.name || 'Tournament'} - ${team.name}`,
          razorpayKey, // Pass the Razorpay key from API response
          userDetails,
          async (response: any) => {
            // Payment successful
            console.log('🔍 Payment flow - Payment successful');
            
            // ✅ FLOW-SPECIFIC NAVIGATION AFTER SUCCESSFUL PAYMENT:
            // DUES FLOW: Navigate back to dues listing page
            // PAY NOW FLOW: Navigate to tournaments page
            // RECORD PAYMENT FLOW: Navigate to tournaments page
            if (paymentSource === 'dues') {
              console.log('🔍 Payment flow - Navigating back to dues after successful payment');
              navigate('/payment-transactions/dues');
            } else {
              // Navigate directly to tournaments page (upcoming tournaments tab)
              navigate('/tournaments');
            }
          },
          (error: any) => {
            // Payment failed - Enhanced error handling with text mode
            console.error('🔍 Payment flow - Payment failed:', error);
            
            // Check if error has enhanced failure details
            if (error && error.text) {
              // Use the enhanced failure text
              setPaymentError(error.text);
              console.log('🔍 Payment flow - Using enhanced failure text:', error.text);
              console.log('🔍 Payment flow - Failure status:', error.status);
              console.log('🔍 Payment flow - Failure details:', error.details);
            } else {
              // Fallback to generic error message
              setPaymentError('Payment failed. Please try again.');
            }
            setIsProcessingPayment(false);
          },
          () => {
            // Payment modal dismissed
            console.log('🔍 Payment flow - Payment modal dismissed');
            setIsProcessingPayment(false);
          }
        );
      
      // Add a timeout to check if Razorpay modal opened
      setTimeout(() => {
        if (isProcessingPayment) {
          console.log('🔍 Payment flow - Razorpay modal may not have opened, checking...');
          // If still processing after 5 seconds, the modal might not have opened
          setPaymentError('Payment modal did not open. Please try again or contact support.');
          setIsProcessingPayment(false);
        }
      }, 5000);
      } catch (razorpayError: any) {
        console.error('🔍 Payment flow - Razorpay initiation failed:', razorpayError);
        
        // Enhanced error handling for Razorpay initiation failures
        if (razorpayError && razorpayError.text) {
          setPaymentError(razorpayError.text);
          console.log('🔍 Payment flow - Razorpay initiation failure details:', razorpayError);
        } else {
          setPaymentError('Failed to initiate payment. Please try again.');
        }
        setIsProcessingPayment(false);
      }

    } catch (error: any) {
      console.error('🔍 Payment flow - Error during payment process:', error);
      // SECURITY: Sanitize error messages to prevent information disclosure
      const sanitizedError = error.message && error.message.includes('API') 
        ? 'Payment process failed. Please try again or contact support.'
        : error.message || 'Payment process failed. Please try again.';
      setPaymentError(sanitizedError);
      setIsProcessingPayment(false);
    }
  };

  // Calculate payment amount based on user's selection
  const calculatePaymentAmount = (): number => {
    if (teamPayments.length === 0) return 0;
    
    const team = teamPayments[0];
    
    // If we have real payment preview data, use it for calculations
    if (paymentPreviewData) {
      if (team.paymentOption === 'total') {
        if (paymentSource === 'dues') {
          // For dues payments: use balanceAmount as the total amount to pay (user wants to pay the remaining balance)
          return paymentPreviewData.balanceAmount || 0;
        } else if (location.state?.isRecordPayment || isRecordPayment || location.state?.source === 'teamRegistration' || location.state?.source === 'playerManagement') {
          // ✅ For record payments AND Pay Now flow: use balanceAmount since user has already paid some amount
          console.log('🔍 calculatePaymentAmount - Record payment/Pay Now using balanceAmount:', paymentPreviewData.balanceAmount);
          return paymentPreviewData.balanceAmount || 0;
        } else {
          // For regular payments: use fullAmount as the total amount to pay
          return paymentPreviewData.fullAmount || paymentPreviewData.totalAmount || 0;
        }
      } else if (team.paymentOption === 'custom' && team.customAmount && team.customAmount > 0) {
        // Enter another amount: Use user's custom amount
        return team.customAmount;
      } else {
        if (paymentSource === 'dues') {
          // For dues payments: use amountToPay as the minimum amount to pay
          return paymentPreviewData.amountToPay || paymentPreviewData.minimumAmountToPay || 0;
        } else {
          // For non-dues payments: use minimumAmountToPay as the minimum amount to pay
          return paymentPreviewData.minimumAmountToPay || 0;
        }
      }
    }
    
    // Fallback to existing calculation logic
    if (team.paymentOption === 'total') {
      return team.revisedAmount || 0;
    } else if (team.paymentOption === 'custom' && team.customAmount && team.customAmount > 0) {
      return team.customAmount;
    } else {
      // Pay minimum - use exact fallback amount
      return team.firstPayment || 0;
    }
  };



  // Calculate total amount for display (alias for backward compatibility)
  const calculateTotalAmount = calculatePaymentAmount;

  const totalAmount = calculateTotalAmount();

  // Record payment function for offline payments
  const handleRecordPayment = async () => {
    if (isProcessingPayment) return;
    
    // Rate limiting check
    const now = Date.now();
    if (now - lastPaymentAttempt < PAYMENT_COOLDOWN_MS) {
      setPaymentError('Please wait before making another payment attempt.');
      return;
    }
    
    setIsProcessingPayment(true);
    setPaymentError(null);
    setLastPaymentAttempt(now);
    
    try {
      const team = teamPayments[0];
      const amountToPay = calculatePaymentAmount();
      
      // Validate custom amount if selected
      if (team.paymentOption === 'custom') {
        if (team.customAmountError) {
          throw new Error(team.customAmountError);
        }
      }
      
      // Get data from location state
      console.log('🔍 Record Payment - Full location.state:', location.state);
      
      const { 
        team: teamData, 
        tournament,
        allPlayers,
        selectedPlayers,
        unpaidPlayerIds,
        hasExistingRegistration,
        registrationId
      } = location.state;
      
      console.log('🔍 Record Payment - Extracted data:', {
        allPlayers,
        selectedPlayers,
        unpaidPlayerIds,
        hasExistingRegistration,
        registrationId
      });
      
      // ✅ FIX: Enhanced validation and debugging for record payment
      let playerIds = allPlayers;
      // ✅ UNIFIED: Player IDs should already be resolved in the main handlePayNow function
      // This fallback logic is now handled in the unified player ID resolution above
      if (!playerIds || playerIds.length === 0) {
        console.error('🚨 Record Payment - No player data found in any source');
        throw new Error('No player data found for payment recording. Please refresh and try again.');
      }
      
      console.log('🔍 Record Payment - Final player IDs resolved:', playerIds);
      
      // ✅ Enhanced validation
      if (!tournament || !tournament._id) {
        throw new Error('Tournament information is missing. Please refresh and try again.');
      }
      
      if (!teamData || !teamData._id) {
        throw new Error('Team information is missing. Please refresh and try again.');
      }
      
      if (!playerIds || playerIds.length === 0) {
        throw new Error('No players selected for payment recording. Please select players and try again.');
      }
      
      if (!amountToPay || amountToPay <= 0) {
        throw new Error('Invalid payment amount. Please check the amount and try again.');
      }
      
      if (!paymentMethod) {
        throw new Error('Payment method is required for record payment. Please select a payment method.');
      }
      
      const requestData: any = {
        registrationId: hasExistingRegistration ? registrationId : null,
        ageCategory: teamData.ageGroups?.[0] || 'U10',
        players: playerIds,
        amountPaid: amountToPay,
        paymentMode: 'offline',
        method: paymentMethod // Include payment method for offline payments
      };
      
      console.log('🔍 Record Payment - Request data:', requestData);
      console.log('🔍 Record Payment - Validation passed, calling API...');
      
      // Call add registration API
      const response = await addTournamentRegistration(
        tournament._id,
        teamData._id,
        requestData
      );
      
      console.log('🔍 Record Payment - Success response:', response);
      
      // ✅ Validate API response
      if (!response || !response.data) {
        throw new Error('Invalid response from server. Please try again.');
      }
      
      if (response.statusCode !== 200 && response.status !== 'success') {
        throw new Error(response.message || 'Payment recording failed on server. Please try again.');
      }
      
      // Show success popup
      setShowSuccessPopup(true);
      
      // Auto-hide popup and navigate after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
        navigate(`/registered-tournaments/${tournamentId}`);
      }, 3000);
      
    } catch (error: any) {
      console.error('🚨 Record Payment Error Details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data,
        stack: error.stack
      });
      
      let errorMessage = 'Payment process failed. Please try again or contact support.';
      
      // Enhanced error message based on error type
      if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || 'Invalid request data. Please check your selection and try again.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please login and try again.';
      } else if (error.response?.status === 403) {
        errorMessage = 'Access denied. You may not have permission to record payments for this tournament.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Tournament or team not found. Please refresh the page and try again.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error occurred. Please try again later or contact support.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setPaymentError(errorMessage);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Validation functions for custom amount
  const validateCustomAmount = (teamId: string) => {
    setTeamPayments(prevTeams => 
      prevTeams.map(team => {
        if (team._id === teamId) {
          const customAmount = team.customAmount;
          const maxAmount = paymentPreviewData?.fullAmount || paymentPreviewData?.totalAmount || 999999;
          
          let error = '';
          
          if (!customAmount || customAmount <= 0) {
            error = 'Please enter a valid amount';
          } else if (customAmount > maxAmount) {
            error = `Amount cannot exceed ₹${maxAmount.toLocaleString()}`;
          } else if (!/^\d+(\.\d{1,2})?$/.test(customAmount.toString())) {
            error = 'Please enter a valid amount (no special characters like 1234, 1435)';
          }
          
          return {
            ...team,
            customAmountError: error,
            customAmountTouched: true
          };
        }
        return team;
      })
    );
  };

  // ✅ DEBUG: Log payment preview data changes
  useEffect(() => {
    if (paymentPreviewData) {
      console.log('🔍 Payment Preview Data:', {
        amountPaid: paymentPreviewData.amountPaid,
        minimumAmountToPay: paymentPreviewData.minimumAmountToPay,
        fullAmount: paymentPreviewData.fullAmount,
        totalAmount: paymentPreviewData.totalAmount,
        balanceAmount: paymentPreviewData.balanceAmount,
        isRecordPayment
      });
    }
  }, [paymentPreviewData, isRecordPayment]);

  if (loading) {
    return (
      <div className="payment-page">
        <Container fluid>
          <div className="loading-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="payment-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              {
                label: 'Tournaments',
                onClick: () => navigate('/tournaments')
              },
              {
                label: paymentSource === 'dues' ? 'Payment Dues' : (paymentSource === 'register' ? 'Register Teams' : 'Add Players'),
                onClick: () => {
                  console.log('🔍 Breadcrumbs clicked - paymentSource:', paymentSource);
                  console.log('🔍 Breadcrumbs clicked - location.state:', location.state);
                  
                  // Check if we came from Record Payment flow (RegisteredTournamentDetail)
                  if (location.state?.isRecordPayment) {
                    console.log('🔍 Breadcrumbs navigating back to RegisteredTournamentDetail');
                    // Extract tournament ID from the current URL path
                    const pathParts = location.pathname.split('/');
                    const tournamentId = pathParts[2]; // /registered-tournaments/{tournamentId}/teams/...
                    navigate(`/registered-tournaments/${tournamentId}`);
                  }
                  // Check if we came from Dues
                  else if (paymentSource === 'dues') {
                    console.log('🔍 Breadcrumbs navigating back to Dues');
                    navigate('/payment-transactions/dues');
                  }
                  // Check if we came from TeamRegistration (has selectedTeams or source is teamRegistration)
                  else if ((location.state?.selectedTeams && location.state.selectedTeams.length > 0) || 
                           location.state?.source === 'teamRegistration' || 
                           paymentSource === 'register') {
                    console.log('🔍 Breadcrumbs navigating back to Register Teams');
                    navigate(`/tournaments/${tournamentId}/register`);
                  } else {
                    // Pass team data back to PlayerManagement screen
                    const teamData = location.state?.team;
                    const teamId = location.state?.teamId || location.pathname.split('/')[4];
                    
                    if (teamData && teamId) {
                      console.log('🔍 PaymentPage - Breadcrumbs navigating back with team data:', teamData);
                      navigate(`/tournaments/${tournamentId}/teams/${teamId}/players`, {
                        state: {
                          team: teamData,
                          teamId: teamId,
                          source: 'payment',
                          tournament: location.state?.tournament
                        }
                      });
                    } else {
                      console.log('🔍 PaymentPage - Breadcrumbs no team data available, navigating without state');
                      navigate(`/tournaments/${tournamentId}/teams/${teamId || ''}/players`);
                    }
                  }
                }
              },
              {
                label: 'Payments',
                active: true
              }
            ]} 
          />
        </div>


        {/* Page Header */}
        <div className="page-header-section mb-4">
          <div className="d-flex align-items-center">
            <button 
              className="back-button me-3"
              onClick={() => {
                console.log('🔍 Back button clicked - paymentSource:', paymentSource);
                console.log('🔍 Back button clicked - location.state:', location.state);
                
                // Check if we came from Record Payment flow (RegisteredTournamentDetail)
                if (location.state?.isRecordPayment) {
                  console.log('🔍 Navigating back to RegisteredTournamentDetail');
                  // Extract tournament ID from the current URL path
                  const pathParts = location.pathname.split('/');
                  const tournamentId = pathParts[2]; // /registered-tournaments/{tournamentId}/teams/...
                  navigate(`/registered-tournaments/${tournamentId}`);
                }
                // Check if we came from Dues
                else if (paymentSource === 'dues') {
                  console.log('🔍 Navigating back to Dues');
                  navigate('/payment-transactions/dues');
                }
                // Check if we came from TeamRegistration (has selectedTeams or source is teamRegistration)
                else if ((location.state?.selectedTeams && location.state.selectedTeams.length > 0) || 
                         location.state?.source === 'teamRegistration' || 
                         paymentSource === 'register') {
                  console.log('🔍 Navigating back to Register Teams');
                  navigate(`/tournaments/${tournamentId}/register`);
                } else {
                  // Pass team data back to PlayerManagement screen
                  const teamData = location.state?.team;
                  const teamId = location.state?.teamId;
                  
                  if (teamData && teamId) {
                    console.log('🔍 PaymentPage - Navigating back with team data:', teamData);
                    navigate(`/tournaments/${tournamentId}/teams/${teamId}/players`, {
                      state: {
                        team: teamData,
                        teamId: teamId,
                        source: 'payment',
                        tournament: location.state?.tournament
                      }
                    });
                  } else {
                    console.log('🔍 PaymentPage - No team data available, navigating without state');
                    navigate(`/tournaments/${tournamentId}/teams/${teamId || ''}/players`);
                  }
                }
              }}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <div>
              <h1 className="page-title mb-0">{tournament?.name || 'Tournament'}</h1>
              {paymentSource === 'register' && (
                <p className="text-muted mb-0">
                  <i className="ri-bank-card-line me-2"></i>
                  Complete your payment to secure your team's spot
                </p>
              )}
              {paymentSource === 'playerManagement' && (
                <p className="text-muted mb-0">
                  <i className="ri-user-add-line me-2"></i>
                  Payment for selected players
                </p>
              )}
              {paymentSource === 'dues' && (
                <p className="text-muted mb-0">
                  <i className="ri-money-dollar-circle-line me-2"></i>
                  Payment for outstanding dues
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Payment Preview Card - Removed for Record Payment Flow as requested */}

        {/* ✅ RENDERING SECTION 1: REGISTER FLOW ONLY */}
        {/* REGISTER FLOW: Uses this section for team registration payments */}
        {/* Team Payment Card - Single Card for Register Flow */}
        {paymentSource === 'register' && teamPayments.length > 0 && teamPayments[0] && (
          <Row>
            <Col lg={12} className="mb-4">
              <Card className="team-payment-card">
                <Card.Body>
                  <div className="team-header">
                    <div className="d-flex align-items-center">
                      <div className="team-logo-small me-3">
                        {teamPayments[0].logo ? (
                          <img 
                            src={getImageUrl(teamPayments[0].logo)}
                            alt={teamPayments[0].name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('d-none');
                            }}
                          />
                        ) : null}
                        {!teamPayments[0].logo && (
                          <div className="default-team-logo-small">
                            <i className="ri-team-line"></i>
                          </div>
                        )}
                      </div>
                      <h5 className="team-name mb-0">{teamPayments[0].name} - {teamPayments[0].ageGroup}</h5>
                    </div>
                  </div>

                  <div className="payment-details">
                    {/* FIXED: Use API data for payment details display */}
                    {/* "Players added" now shows totalAmount from API instead of just player count */}
                    {/* "Per Player Registration Fee" now shows perPlayerFee from API */}
                    <div className="detail-row">
                      <span>{teamPayments[0].playersCount} Players added</span>
                      <span className="amount">₹{(() => {
                        if (paymentPreviewData) {
                          return (paymentPreviewData.totalAmount || 0);
                        } else {
                          return (teamPayments[0].playersCount * teamPayments[0].playerFee) || 0;
                        }
                      })().toLocaleString()}</span>
                    </div>
                    {/* Per Player Registration Fee for register flow */}
                    <div className="detail-row">
                      <span>Per Player Registration Fee</span>
                      <span className="amount">₹{((paymentPreviewData ? paymentPreviewData.perPlayerFee : teamPayments[0].playerFee) || 0).toLocaleString()}</span>
                    </div>
                    {/* Payment Details Section */}
                    <div className="payment-details">
                      {paymentPreviewData ? (
                        <>
                          <div className="detail-row">
                            <span>Amount Already Paid</span>
                            <span className="amount text-success">₹{(paymentPreviewData.amountPaid || 0).toLocaleString()}</span>
                          </div>
                          <div className="detail-row">
                            <span>Minimum Amount Required</span>
                            <span className="amount">₹{(paymentPreviewData.minimumAmountToPay || 0).toLocaleString()}</span>
                          </div>
                          <div className="detail-row">
                            <span>Full Amount</span>
                            <span className="amount">₹{(paymentPreviewData.fullAmount || paymentPreviewData.totalAmount || 0).toLocaleString()}</span>
                          </div>
                          <div className="detail-row">
                            <span>Balance Amount</span>
                            <span className="amount">₹{(paymentPreviewData.balanceAmount || 0).toLocaleString()}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="detail-row">
                            <span>First Payment: 25%</span>
                            <span className="amount">₹{(teamPayments[0].firstPayment || 0).toLocaleString()}</span>
                          </div>
                          <div className="detail-row">
                            <span>Remaining Amount</span>
                            <span className="amount">₹{(teamPayments[0].secondPayment || 0).toLocaleString()}</span>
                          </div>
                        </>
                      )}

                      {/* Hide Due date for record payments */}
                      {!isRecordPayment && (
                        <div className="detail-row due-date">
                          <span>Due on: {teamPayments[0].dueDate}</span>
                        </div>
                      )}
                    </div>

                    {/* ✅ FIX: Show Payment Complete message for both flows when balance is 0 */}
                    {paymentPreviewData && (Number(paymentPreviewData.balanceAmount) === 0 || paymentPreviewData.balanceAmount === null) ? (
                      <div className="alert alert-success mb-3" role="alert" style={{backgroundColor: '#d4edda', borderColor: '#c3e6cb', color: '#155724', fontSize: '16px', padding: '15px', margin: '10px 0'}}>
                        <i className="ri-check-circle-line me-2"></i>
                        <strong>Payment Complete!</strong> All fees have been paid for this team.
                      </div>
                    ) : (
                      <div className="payment-options">
                        <h6 className="options-title">
                          {isRecordPayment ? 'Payment Options' : 'Choose Payments'}
                        </h6>
                        
                        <div className="payment-options-list">
                          <div className="payment-option-item">
                            <div 
                              className={`payment-option ${teamPayments[0].paymentOption === 'total' ? 'selected' : ''}`}
                              onClick={() => handlePaymentOptionChange(teamPayments[0]._id, 'total')}
                            >
                              <div className="option-content">
                                <Form.Check
                                  type="radio"
                                  id={`total-${teamPayments[0]._id}`}
                                  name={`payment-${teamPayments[0]._id}`}
                                  checked={teamPayments[0].paymentOption === 'total'}
                                  onChange={() => handlePaymentOptionChange(teamPayments[0]._id, 'total')}
                                  className="payment-radio"
                                />
                                <span>Pay Total</span>
                              </div>
                              <div className="option-right">
                                <Badge bg="primary" className="recommended-badge">Recommended</Badge>
                                <span className="amount">₹{((paymentPreviewData ? (location.state?.isRecordPayment ? paymentPreviewData.balanceAmount : (paymentPreviewData.fullAmount || paymentPreviewData.totalAmount)) : teamPayments[0].revisedAmount) || 0).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>

                          <div className="payment-option-item">
                            <div 
                              className={`payment-option ${teamPayments[0].paymentOption === 'minimum' ? 'selected' : ''}`}
                              onClick={() => handlePaymentOptionChange(teamPayments[0]._id, 'minimum')}
                            >
                              <div className="option-content">
                                <Form.Check
                                  type="radio"
                                  id={`minimum-${teamPayments[0]._id}`}
                                  name={`payment-${teamPayments[0]._id}`}
                                  checked={teamPayments[0].paymentOption === 'minimum'}
                                  onChange={() => handlePaymentOptionChange(teamPayments[0]._id, 'minimum')}
                                  className="payment-radio"
                                />
                                <span>Pay Minimum</span>
                              </div>
                              <div className="option-right">
                                <span className="amount">₹{((paymentPreviewData ? paymentPreviewData.minimumAmountToPay : teamPayments[0].firstPayment) || 0).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>

                          <div className="payment-option-item">
                            <div 
                              className={`payment-option ${teamPayments[0].paymentOption === 'custom' ? 'selected' : ''}`}
                              onClick={(e) => {
                                const target = e.target as HTMLElement;
                                if (e.target === e.currentTarget || target.closest('.option-content')) {
                                  handleCustomAmountFocus(teamPayments[0]._id);
                                }
                              }}
                            >
                              <div className="option-content">
                                <Form.Check
                                  type="radio"
                                  id={`custom-${teamPayments[0]._id}`}
                                  name={`payment-${teamPayments[0]._id}`}
                                  checked={teamPayments[0].paymentOption === 'custom'}
                                  onChange={() => handleCustomAmountFocus(teamPayments[0]._id)}
                                  className="payment-radio"
                                />
                                <span>Enter Another Amount</span>
                              </div>
                              <div className="option-right">
                                <Form.Control
                                  type="text"
                                  placeholder="Enter amount"
                                  value={teamPayments[0].customAmount || ''}
                                  onChange={(e) => handleCustomAmountChange(teamPayments[0]._id, Number(e.target.value))}
                                  onFocus={() => handleCustomAmountFocus(teamPayments[0]._id)}
                                  className="custom-amount-input"
                                />
                              </div>
                            </div>
                            {teamPayments[0].customAmountError && (
                              <div className="error-message">
                                {teamPayments[0].customAmountError}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Payment Method Selection for Record Payment */}
                        {isRecordPayment && (
                          <div className="payment-method-section mb-4">
                            <h6 className="mb-3">Payment Mode</h6>
                            <Select
                              classNamePrefix="react-select"
                              value={paymentMethodOptions.find(option => option.value === paymentMethod)}
                              onChange={(selectedOption) => {
                                setPaymentMethod(selectedOption ? selectedOption.value : 'cash');
                              }}
                              options={paymentMethodOptions}
                              placeholder="Select payment mode"
                              isSearchable={false}
                              menuPortalTarget={document.body}
                              styles={{
                                control: (base) => ({ 
                                  ...base, 
                                  minHeight: '45px',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '8px'
                                }),
                                menu: (base) => ({ 
                                  ...base, 
                                  zIndex: 99999,
                                  maxHeight: 400,
                                  overflow: 'hidden'
                                }),
                                menuPortal: (base) => ({ 
                                  ...base, 
                                  zIndex: 99999 
                                }),
                                menuList: (base) => ({
                                  ...base,
                                  maxHeight: 400,
                                  padding: 0,
                                  overflowY: 'auto',
                                  overflowX: 'hidden'
                                })
                              }}
                            />
                          </div>
                        )}

                        <div className="total-amount-display mb-3">
                          <div className="detail-row">
                            <span><strong>Total Amount:</strong></span>
                            <span className="amount">₹{(totalAmount || 0).toLocaleString()}</span>
                          </div>
                        </div>
                        
                        {/* Error Message Display */}
                        {paymentError && (
                          <div className="alert alert-danger mb-3" role="alert">
                            <i className="ri-error-warning-line me-2"></i>
                            {paymentError}
                          </div>
                        )}
                        
                        {/* Record Payment Button */}
                        {(isRecordPayment || location.state?.isRecordPayment) ? (
                          <Button
                            variant="primary"
                            size="lg"
                            onClick={handleRecordPayment}
                            disabled={isProcessingPayment || (teamPayments[0]?.paymentOption === 'custom' && !!teamPayments[0]?.customAmountError && !(location.state?.isRecordPayment || isRecordPayment))}
                            className="payment-btn w-100"
                          >
                            {isProcessingPayment ? (
                              <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                Recording Payment...
                              </>
                            ) : (
                              'Record Payment'
                            )}
                          </Button>
                        ) : (
                          <Button 
                            variant="primary" 
                            onClick={handlePayNow}
                            disabled={getPaymentButtonState().disabled}
                            className="pay-secure-btn"
                            title={getPaymentButtonState().disabled ? getPaymentButtonState().text : 'Proceed to secure payment'}
                          >
                            {isProcessingPayment ? (
                              <>
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                Processing...
                              </>
                            ) : (
                              <>
                                <i className="ri-lock-line me-2"></i>
                                {getPaymentButtonState().text}
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                    
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* ✅ RENDERING SECTION 2: DUES FLOW, PAY NOW FLOW & RECORD PAYMENT FLOW */}
        {/* DUES FLOW: Uses this section for payment preview display (consistent layout) */}
        {/* PAY NOW FLOW: Uses this section when coming from team registration or player management */}
        {/* RECORD PAYMENT FLOW: Uses this section for recording offline payments */}
        {/* Team Payment Cards - Full Row for PlayerManagement Flow */}
        {(paymentSource === 'playerManagement' || paymentSource === 'dues') && teamPayments.length > 0 && (
          <Row>
            {teamPayments.map((team) => (
              <Col key={team._id} lg={12} className="mb-4">
                <Card className="team-payment-card">
                  <Card.Body>
                    <div className="team-header">
                      <div className="d-flex align-items-center">
                        <div className="team-logo-small me-3">
                          {team.logo ? (
                            <img 
                              src={getImageUrl(team.logo)}
                              alt={team.name}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('d-none');
                              }}
                            />
                          ) : null}
                          {!team.logo && (
                            <div className="default-team-logo-small">
                              <i className="ri-team-line"></i>
                            </div>
                          )}
                        </div>
                        <h5 className="team-name mb-0">{team.name} - {team.ageGroup}</h5>
                      </div>
                    </div>

                    <div className="payment-details">
                      <div className="detail-row">
                        <span>{team.playersCount} Players added</span>
                        <span className="amount">₹{((paymentPreviewData ? paymentPreviewData.totalAmount : (team.playersCount * team.playerFee)) || 0).toLocaleString()}</span>
                      </div>
                      <div className="detail-row">
                        <span>Per Player Registration Fee</span>
                        <span className="amount">₹{((paymentPreviewData ? paymentPreviewData.perPlayerFee : team.playerFee) || 0).toLocaleString()}</span>
                      </div>
                      {paymentPreviewData ? (
                        <>
                          <div className="detail-row">
                            <span>Amount Already Paid</span>
                            <span className="amount text-success">₹{(paymentPreviewData.amountPaid || 0).toLocaleString()}</span>
                          </div>
                          <div className="detail-row">
                            <span>Minimum Amount Required</span>
                            <span className="amount">₹{(paymentPreviewData.minimumAmountToPay || 0).toLocaleString()}</span>
                          </div>
                          <div className="detail-row">
                            <span>Full Amount</span>
                            <span className="amount">₹{(paymentPreviewData.fullAmount || paymentPreviewData.totalAmount || 0).toLocaleString()}</span>
                          </div>
                          <div className="detail-row">
                            <span>Balance Amount</span>
                            <span className="amount">₹{(paymentPreviewData.balanceAmount || 0).toLocaleString()}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="detail-row">
                            <span>First Payment: 25%</span>
                            <span className="amount">₹{(team.firstPayment || 0).toLocaleString()}</span>
                          </div>
                          <div className="detail-row">
                            <span>Remaining Amount</span>
                            <span className="amount">₹{(team.secondPayment || 0).toLocaleString()}</span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Payment Complete Message when balance is 0 - PlayerManagement Flow */}
                    {paymentPreviewData && (Number(paymentPreviewData.balanceAmount) === 0 || paymentPreviewData.balanceAmount === null) && (
                      <div className="alert alert-success mb-3" role="alert" style={{backgroundColor: '#d4edda', borderColor: '#c3e6cb', color: '#155724', fontSize: '16px', padding: '15px', margin: '10px 0'}}>
                        <i className="ri-check-circle-line me-2"></i>
                        <strong>Payment Complete!</strong> All fees have been paid for this team.
                      </div>
                    )}

                    <div className={`payment-options ${isBalanceZero() ? 'd-none' : ''}`}>
                      <h6 className="options-title">
                        {isRecordPayment ? 'Payment Options' : 'Choose Payments'}
                      </h6>
                      
                      <div className="payment-options-list">
                        <div className="payment-option-item">
                          <div 
                            className={`payment-option ${team.paymentOption === 'total' ? 'selected' : ''}`}
                            onClick={() => handlePaymentOptionChange(team._id, 'total')}
                          >
                            <div className="option-content">
                              <Form.Check
                                type="radio"
                                id={`total-${team._id}`}
                                name={`payment-${team._id}`}
                                checked={team.paymentOption === 'total'}
                                onChange={() => handlePaymentOptionChange(team._id, 'total')}
                                className="payment-radio"
                              />
                              <span>Pay Total</span>
                            </div>
                            <div className="option-right">
                              <Badge bg="primary" className="recommended-badge">Recommended</Badge>
                              <span className="amount">₹{((paymentPreviewData ? (paymentSource === 'dues' ? paymentPreviewData.balanceAmount : (location.state?.isRecordPayment || location.state?.source === 'teamRegistration' || location.state?.source === 'playerManagement' ? (console.log('🔍 Pay Total - Using balanceAmount for Pay Now flow:', paymentPreviewData.balanceAmount), paymentPreviewData.balanceAmount) : (paymentPreviewData.fullAmount || paymentPreviewData.totalAmount))) : team.revisedAmount) || 0).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>

                        <div className="payment-option-item">
                          <div 
                            className={`payment-option ${team.paymentOption === 'minimum' ? 'selected' : ''}`}
                            onClick={() => handlePaymentOptionChange(team._id, 'minimum')}
                          >
                            <div className="option-content">
                              <Form.Check
                                type="radio"
                                id={`minimum-${team._id}`}
                                name={`payment-${team._id}`}
                                checked={team.paymentOption === 'minimum'}
                                onChange={() => handlePaymentOptionChange(team._id, 'minimum')}
                                className="payment-radio"
                              />
                              <span>Pay Minimum</span>
                            </div>
                            <div className="option-right">
                              <span className="amount">₹{((paymentPreviewData ? paymentPreviewData.minimumAmountToPay : team.firstPayment) || 0).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>

                        <div className="payment-option-item">
                          <div 
                            className={`payment-option ${team.paymentOption === 'custom' ? 'selected' : ''}`}
                            onClick={(e) => {
                              // Prevent card click when clicking on input
                              const target = e.target as HTMLElement;
                              if (e.target === e.currentTarget || target.closest('.option-content')) {
                                handleCustomAmountFocus(team._id);
                              }
                            }}
                          >
                            <div className="option-content">
                              <div className="d-flex align-items-start w-100">
                                <Form.Check
                                  type="radio"
                                  id={`custom-${team._id}`}
                                  name={`payment-${team._id}`}
                                  checked={team.paymentOption === 'custom'}
                                  onChange={() => handleCustomAmountFocus(team._id)}
                                  className="payment-radio me-3"
                                />
                                <div className="custom-amount-section flex-grow-1">
                                  <Form.Control
                                    type="text"
                                    placeholder="Enter custom amount"
                                    value={team.customAmount && team.customAmount > 0 ? formatAmount(team.customAmount) : ''}
                                    onChange={(e) => handleFormattedAmountChange(team._id, e.target.value)}
                                    onFocus={() => handleCustomAmountFocus(team._id)}
                                    onBlur={() => handleCustomAmountBlur(team._id)}
                                    onClick={(e) => e.stopPropagation()}
                                    className={`custom-amount-input ${team.customAmountError && team.customAmountTouched ? 'is-invalid' : ''}`}
                                  />
                                  {team.customAmountError && team.customAmountTouched && (
                                    <div className="text-danger small mt-1 validation-error">{team.customAmountError}</div>
                                  )}
                                  
                                  {/* Suggested amounts */}
                                  {team.paymentOption === 'custom' && (
                                    <div className="suggested-amounts">
                                      <small>Suggested amounts:</small>
                                      <div className="suggested-buttons">
                                        {getSuggestedAmounts(team).map((amount, index) => (
                                          <button
                                            key={index}
                                            type="button"
                                            className="btn btn-outline-primary btn-sm suggested-amount-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCustomAmountChange(team._id, amount);
                                            }}
                                          >
                                            ₹{formatAmount(amount)}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>


                      {/* Payment Method Selection for Record Payment */}
                      {isRecordPayment && (
                        <div className={`payment-method-section mb-4 ${isBalanceZero() ? 'd-none' : ''}`}>
                          <h6 className="mb-3">Payment Mode</h6>
                          <Select
                            classNamePrefix="react-select"
                            value={paymentMethodOptions.find(option => option.value === paymentMethod)}
                            onChange={(selectedOption) => {
                              setPaymentMethod(selectedOption ? selectedOption.value : 'cash');
                            }}
                            options={paymentMethodOptions}
                            placeholder="Select payment mode"
                            isSearchable={false}
                            menuPortalTarget={document.body}
                            styles={{
                              control: (base) => ({ 
                                ...base, 
                                minHeight: '45px',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px'
                              }),
                              menu: (base) => ({ 
                                ...base, 
                                zIndex: 99999,
                                maxHeight: 400,
                                overflow: 'hidden'
                              }),
                              menuPortal: (base) => ({ 
                                ...base, 
                                zIndex: 99999 
                              }),
                              menuList: (base) => ({
                                ...base,
                                maxHeight: 400,
                                padding: 0,
                                overflowY: 'auto',
                                overflowX: 'hidden'
                              })
                            }}
                          />
                        </div>
                      )}
                      <div className={`total-amount-display mb-3 ${isBalanceZero() ? 'd-none' : ''}`}>
                        <div className="detail-row">
                          <span><strong>Total Amount:</strong></span>
                          <span className="amount">₹{(totalAmount || 0).toLocaleString()}</span>
                        </div>
                      </div>
                      
                      {/* Error Message Display */}
                      {paymentError && (
                        <div className="alert alert-danger mb-3" role="alert">
                          <i className="ri-error-warning-line me-2"></i>
                          {paymentError}
                        </div>
                      )}

                      
                      <Button 
                        variant="primary" 
                        onClick={handlePayNow}
                        disabled={getPaymentButtonState().disabled}
                        className={`pay-secure-btn ${isBalanceZero() ? 'd-none' : ''}`}
                        title={getPaymentButtonState().disabled ? getPaymentButtonState().text : 'Proceed to secure payment'}
                      >
                        {isProcessingPayment ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            Processing...
                          </>
                        ) : (
                          <>
                        <i className="ri-lock-line me-2"></i>
                        {getPaymentButtonState().text}
                          </>
                        )}
                      </Button>
                    </div>

                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* Fallback Content - Show when no data is available */}
        {(!teamPayments || teamPayments.length === 0) && (
          <Row>
            <Col lg={12}>
              <Card className="text-center p-5">
                <Card.Body>
                  <div className="mb-3">
                    <i className="ri-error-warning-line text-warning" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5>No Payment Data Available</h5>
                  <p className="text-muted mb-3">
                    Unable to load payment information. Please go back and try again.
                  </p>
                  <div className="d-flex justify-content-center gap-3">
                    <Button 
                      variant="outline-secondary" 
                      onClick={() => navigate(`/tournaments/${tournamentId}/register`)}
                    >
                      <i className="ri-arrow-left-line me-2"></i>
                      Back to Register Teams
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={() => window.location.reload()}
                    >
                      <i className="ri-refresh-line me-2"></i>
                      Reload Page
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Success Popup for Record Payment */}
        {showSuccessPopup && isRecordPayment && (
          <div className="success-popup-overlay">
            <div className="success-popup">
              <div className="success-popup-content">
                <div className="success-icon">
                  <i className="ri-check-line"></i>
                </div>
                <h4>Payment Recorded Successfully!</h4>
                <p>The payment has been recorded for the selected players.</p>
                <div className="success-details">
                  <p><strong>Amount:</strong> ₹{calculatePaymentAmount().toLocaleString()}</p>
                                        <p><strong>Mode:</strong> {paymentMethodOptions.find(opt => opt.value === paymentMethod)?.label}</p>
                </div>
              </div>
            </div>
          </div>
        )}

      </Container>
    </div>
  );
};

export default PaymentPage;